import { BRAND_SOURCE_KEY } from "@/lib/brandConfig";
import { captureAttribution } from "@/lib/captureAttribution";
import type { PremiumPlansFunnelSnapshot } from "@/config/bioTemplatePremiumPlans";
import { getHeroCtaSlackExtra } from "@/lib/heroCtaSlackContext";
/**
 * PostHog Analytics Integration
 * Comprehensive tracking for user interactions, conversions, and session recording
 */

import posthog from "posthog-js";
import {
  getWorkerGeoAnalyticsProps,
  getGeoConflictEventProps,
  setClientGeoEnrichmentOverlay,
} from "@/lib/serverTemplateDetection";
import { getCanonicalLocation, regionFromCountry } from "@/lib/canonicalLocation";
import { buildPostHogSessionReportUrl } from "../../shared/posthogReportUrl";

// PostHog configuration check
export const isPostHogConfigured = () => {
  return (
    typeof window !== "undefined" &&
    window.posthog &&
    typeof window.posthog.capture === "function"
  );
};

/**
 * Región para tracking — VERDAD ÚNICA: override manual (user_region_manual)
 * o país del Worker vía canonicalLocation. La key legacy user_region ya no
 * se escribe (useRegion dejó de auto-persistirla).
 */
export function getUserRegionForTracking(): string {
  if (typeof window === "undefined") return "LATAM";
  return (
    localStorage.getItem("user_region_manual") ||
    regionFromCountry(getCanonicalLocation().country) ||
    "LATAM"
  );
}

// ─── UTM helpers (shared) ─────────────────────────────────────────────────────

/**
 * PostHog JS automatically captures UTM params and sets them as person
 * properties under standard names:
 *   last-touch  → utm_source, utm_medium, utm_campaign, utm_term, utm_content
 *   first-touch → $initial_utm_source, $initial_utm_medium, ...
 * We only parse UTMs here to fire our custom utm_attribution event.
 * See: https://posthog.com/docs/product-analytics/person-properties#default-person-properties
 */
function parseUTMFromSearch(search: string): Record<string, string> {
  const params = new URLSearchParams(search);
  const keys = [
    "utm_source", "utm_medium", "utm_campaign",
    "utm_term", "utm_content", "utm_id",
    "gclid", "fbclid", "ttclid", "msclkid", "ref",
  ];
  const result: Record<string, string> = {};
  for (const key of keys) {
    const val = params.get(key);
    if (val) result[key] = val;
  }
  return result;
}

// ─── Super Properties ─────────────────────────────────────────────────────────

/**
 * Register super properties that PostHog automatically attaches to EVERY event.
 * Call once after PostHog loads. Avoids copy-pasting user_region/test_country
 * across every individual capture() call.
 */
export function registerSuperPropertiesPostHog() {
  if (!isPostHogConfigured()) return;

  const colorScheme = window.matchMedia?.("(prefers-color-scheme: dark)")?.matches
    ? "dark"
    : "light";
  const standalone =
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    (window.navigator as any).standalone === true ||
    window.matchMedia?.("(display-mode: standalone)")?.matches === true;

  // Worker-resolved geo: signal autoritativa de Cloudflare (más precisa que
  // PostHog $geoip_*, que usa MaxMind y a veces deriva del proxy upstream).
  // Antes esto solo se incluía en eventos custom via getWorkerGeoAnalyticsProps()
  // como `extra`, así $pageview/web_vital/scroll_depth no la llevaban. Ahora
  // como super-property TODOS los events la incluyen automáticamente.
  // Si __TEMPLATE_CONFIG__ no se inyectó (CF no corrió, p. ej. SPA-nav),
  // workerGeo es {} y posthog.register skipea los keys undefined.
  const workerGeo = getWorkerGeoAnalyticsProps();

  // Detect geo sim mode (for admin/QA testing USExclusiveLanding or other templates from AR etc. without VPN)
  // These become super-properties so EVERY event (pageview, autocapture, custom) can be filtered by is_geo_sim / simulated_country in PostHog.
  let isGeoSim = false;
  let simulatedCountry: string | null = null;
  let simulateVpn: string | null = null;
  let simulateIp: string | null = null;
  if (typeof window !== "undefined") {
    const p = new URLSearchParams(window.location.search);
    isGeoSim = p.get("geo_sim") === "1" || !!p.get("debug");
    simulatedCountry = p.get("country") || p.get("sim_country") || null;
    simulateVpn = p.get("simulate_vpn") || p.get("vpn_sim") || null;
    simulateIp = p.get("simulate_ip") || null;
  }

  posthog.register({
    site: BRAND_SOURCE_KEY, // distinguishes juaperrone.com from other brand properties
    brand_source_key: BRAND_SOURCE_KEY,
    user_region: getUserRegionForTracking(),
    test_country: localStorage.getItem("test_country") || null,
    app_version: import.meta.env.VITE_APP_VERSION || null,
    /** Session-level hints for device / shell (startup-friendly breakdowns). */
    color_scheme_preference: colorScheme,
    pwa_display_standalone: standalone,
    /** Cloudflare-resolved geo (worker autoritativa). Mejor precisión que
     *  $geoip_* para template routing y filters de admin reports.
     *  Incluye `cta_resolution_source` (kv-hit/db-vercel/edge) para medir
     *  hit-rate del CTA resolver en producción. */
    ...workerGeo,
    // Geo sim / debug mode super props — critical to separate test traffic (e.g. AR testing USExclusive) from real visitors.
    is_geo_sim: isGeoSim || undefined,
    simulated_country: simulatedCountry || undefined,
    simulate_vpn: simulateVpn || undefined,
    simulate_ip: simulateIp || undefined,
  });

  // Template + dual + cascade (state_us/city/country) en super-props desde el
  // primer event — visitor_experience_resolved se dispara en SessionIngressTracker.
  try {
    // Lazy to avoid circular imports with serverTemplateDetection consumers.
    void import("@/lib/visitorExperiencePostHog").then((m) => {
      m.registerVisitorExperienceContextPostHog();
    });
  } catch {
    /* best-effort */
  }

  if (isGeoSim) {
    try {
      posthog.capture("geo_sim_activated", {
        simulated_country: simulatedCountry,
        simulate_vpn: simulateVpn,
        simulate_ip: simulateIp,
        timestamp: new Date().toISOString(),
      });
    } catch {
      // silent
    }
  }

  // Geo conflict event: dispara una sola vez por mount cuando el worker detectó
  // mismatch entre regionCode-derived province y city-derived province (caso
  // Bella Vista BA vs Corrientes). Útil para descubrir nuevos homónimos sin
  // depender de audits manuales periódicos.
  const conflict = getGeoConflictEventProps();
  if (conflict) {
    try {
      posthog.capture(conflict.event, conflict.props);
    } catch {
      // posthog no inicializado / fail silencioso
    }
  }
}

// ─── Person Profile Initialization ───────────────────────────────────────────

/**
 * Call once on PostHog `loaded` callback.
 *
 * PostHog JS already sets automatically (do NOT duplicate):
 *   $browser, $browser_version, $os, $device_type, $browser_language
 *   $screen_height/width, $viewport_height/width
 *   $referrer, $referring_domain, $initial_referrer, $initial_current_url
 *   utm_* (last-touch), $initial_utm_* (first-touch)
 *   $geoip_country_code/name, $geoip_city_name, $geoip_time_zone, etc.
 *
 * We only set properties that PostHog does NOT capture automatically.
 * Uses $set (updated each visit) and $set_once (set only the first time).
 */
export function initPersonPropertiesPostHog() {
  if (!isPostHogConfigured()) return;

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const connection = (navigator as any).connection;

  // ── Session / visit count ──────────────────────────────────────────────────
  // Increment total session count stored in localStorage (persists across tabs).
  // Use sessionStorage flag to count once per browser session, not per page load.
  const SESSION_STARTED_KEY = "ph_session_started";
  const TOTAL_SESSIONS_KEY  = "ph_total_sessions";
  const FIRST_VISIT_KEY     = "ph_first_visit";

  const isNewSession = !sessionStorage.getItem(SESSION_STARTED_KEY);
  if (isNewSession) {
    sessionStorage.setItem(SESSION_STARTED_KEY, "1");
  }
  const totalSessions = isNewSession
    ? parseInt(localStorage.getItem(TOTAL_SESSIONS_KEY) || "0") + 1
    : parseInt(localStorage.getItem(TOTAL_SESSIONS_KEY) || "1");
  if (isNewSession) localStorage.setItem(TOTAL_SESSIONS_KEY, String(totalSessions));

  const firstVisitDate = localStorage.getItem(FIRST_VISIT_KEY) || new Date().toISOString();
  if (!localStorage.getItem(FIRST_VISIT_KEY)) {
    localStorage.setItem(FIRST_VISIT_KEY, firstVisitDate);
  }
  const daysSinceFirstVisit = Math.floor(
    (Date.now() - new Date(firstVisitDate).getTime()) / (1000 * 60 * 60 * 24)
  );

  // $set — updated on every session
  const setProps: Record<string, unknown> = {
    user_region: getUserRegionForTracking(),
    test_country: localStorage.getItem("test_country") || null,
    cookies_enabled: navigator.cookieEnabled,
    do_not_track: navigator.doNotTrack === "1",
    // Network Information API — not captured by PostHog
    connection_type: connection?.effectiveType || null,
    connection_downlink_mbps: connection?.downlink || null,
    // Visit engagement
    session_count: totalSessions,
    days_since_first_visit: daysSinceFirstVisit,
    is_returning_visitor: totalSessions > 1,
  };

  // $set_once — written only when the property doesn't yet exist on the profile
  const setOnceProps: Record<string, unknown> = {
    first_seen: firstVisitDate,
    first_landing_page: window.location.pathname,
    first_user_region:
      localStorage.getItem("user_region_manual") ||
      regionFromCountry(getCanonicalLocation().country) ||
      "LATAM",
  };

  posthog.setPersonProperties(setProps, setOnceProps);
}

// ─── Async IPLocate refresh (PostHog super props) ─────────────────────────────

type GeolocateEnrichResponse = {
  country?: string | null;
  province?: string | null;
  city?: string | null;
  region?: string | null;
  timezone?: string | null;
  isp?: string | null;
  source?: string;
  worker_geo_source?: string;
  privacy?: {
    is_vpn?: boolean;
    is_proxy?: boolean;
    is_tor?: boolean;
    is_relay?: boolean;
    is_hosting?: boolean;
  } | null;
  vpn_detected?: boolean;
  is_hosting?: boolean;
  asn_details?: { name?: string; asn?: string | number } | null;
  from_cache?: boolean;
};

function mapGeolocateToWorkerGeoProps(
  geo: GeolocateEnrichResponse,
): Record<string, string | undefined> {
  const privacy = geo.privacy;
  const vpn =
    geo.vpn_detected === true ||
    !!(
      privacy &&
      (privacy.is_vpn || privacy.is_proxy || privacy.is_tor || privacy.is_relay)
    );
  const hosting = geo.is_hosting === true || !!privacy?.is_hosting;
  const workerSource =
    geo.worker_geo_source ||
    (geo.from_cache ? "cache" : geo.source === "iplocate_io" ? "iplocate" : geo.source) ||
    undefined;
  const country =
    geo.country && geo.country !== "UNKNOWN" ? geo.country : undefined;
  const province = geo.province?.trim() || geo.region?.trim() || undefined;
  const city = geo.city?.trim() || undefined;
  const asnName = geo.isp || geo.asn_details?.name || undefined;
  const parts = [country, province, city].filter(
    (x): x is string => typeof x === "string" && x.length > 0,
  );
  return {
    worker_country: country,
    worker_province: province,
    worker_city: city,
    worker_geo_source: workerSource,
    worker_vpn_detected: vpn ? "true" : undefined,
    worker_is_hosting: hosting ? "true" : undefined,
    worker_asn_organization: asnName,
    worker_timezone: geo.timezone || undefined,
    geo_summary: parts.length ? parts.join(" · ") : undefined,
  };
}

let geoRefreshPromise: Promise<boolean> | null = null;

/** Fire-and-forget IPLocate enrichment via Worker API (tracking only, never blocks UX). */
export function startPostHogGeoRefresh(): void {
  if (geoRefreshPromise || typeof window === "undefined") return;
  geoRefreshPromise = refreshPostHogGeoEnrichment();
}

/** Await enrichment up to maxMs so first $pageview can include VPN/geo flags when cache is warm. */
export async function waitForPostHogGeoEnrichment(maxMs = 400): Promise<void> {
  if (!geoRefreshPromise) return;
  await Promise.race([
    geoRefreshPromise,
    new Promise<void>((resolve) => setTimeout(resolve, maxMs)),
  ]);
}

export async function refreshPostHogGeoEnrichment(): Promise<boolean> {
  if (typeof window === "undefined" || !isPostHogConfigured()) return false;

  const initial = getWorkerGeoAnalyticsProps();
  if (
    initial.worker_geo_source &&
    initial.worker_geo_source !== "cf_only" &&
    initial.worker_vpn_detected !== undefined
  ) {
    return true;
  }

  try {
    const response = await fetch("/api/geolocate?enrich=1", {
      method: "GET",
      headers: { Accept: "application/json" },
      credentials: "same-origin",
    });
    if (!response.ok) return false;

    const geo = (await response.json()) as GeolocateEnrichResponse;
    const geoProps = mapGeolocateToWorkerGeoProps(geo);
    const hasEnrichment = Boolean(
      geoProps.worker_geo_source &&
        geoProps.worker_geo_source !== "cf_only",
    );
    if (!hasEnrichment && !geoProps.worker_vpn_detected) return false;

    // VERDAD ÚNICA DE LOCATION: el Worker manda. El fallback /api/geolocate
    // solo completa campos que el Worker no resolvió (VPN/ASN/hosting) — no
    // pisa country/province/city. `initial` trae la verdad del Worker.
    const merged: Record<string, string | undefined> = { ...geoProps };
    for (const [k, v] of Object.entries(initial)) {
      if (v !== undefined && v !== null && v !== "") merged[k] = v;
    }

    setClientGeoEnrichmentOverlay(merged);

    const registerProps = Object.fromEntries(
      Object.entries(merged).filter(([, v]) => v !== undefined && v !== null),
    );
    posthog.register(registerProps);

    posthog.setPersonProperties({
      is_vpn: merged.worker_vpn_detected === "true",
      is_hosting: merged.worker_is_hosting === "true",
      current_asn: merged.worker_asn_organization || null,
      current_city: merged.worker_city || null,
      current_province: merged.worker_province || null,
      geo_source: merged.worker_geo_source || null,
    });

    posthog.capture("geo_enrichment_refreshed", {
      worker_geo_source: geoProps.worker_geo_source,
      worker_vpn_detected: geoProps.worker_vpn_detected === "true",
      worker_is_hosting: geoProps.worker_is_hosting === "true",
      from_cache: geo.from_cache === true,
      api_source: geo.source || null,
      path: window.location.pathname,
    });

    return true;
  } catch {
    return false;
  }
}

// ─── UTM Attribution ──────────────────────────────────────────────────────────

/**
 * Fire a custom utm_attribution event whenever UTM params are detected.
 * PostHog already sets utm_* person properties automatically — we don't
 * duplicate that here, we just fire the event as a funnel signal.
 */
export function captureUTMAttributionPostHog(
  search: string,
  pathname: string
) {
  if (!isPostHogConfigured()) return;

  const utmParams = parseUTMFromSearch(search);
  if (Object.keys(utmParams).length === 0) return;

  posthog.capture("utm_attribution", {
    ...utmParams,
    page_path: pathname,
    referrer: document.referrer || null,
    timestamp: new Date().toISOString(),
    user_region: getUserRegionForTracking(),
  });
}

// ─── Web Vitals ───────────────────────────────────────────────────────────────

/**
 * Observe Core Web Vitals (LCP, FCP, CLS, TTFB, INP) via PerformanceObserver
 * and send them to PostHog. Call once after PostHog is loaded.
 *
 * Key fix: LCP and CLS are captured on `visibilitychange` (page hide) so we
 * always get the final definitive value, not an intermediate one.
 */
function performanceObserverSupportsEntryType(type: string): boolean {
  const supported = (
    PerformanceObserver as unknown as { supportedEntryTypes?: readonly string[] }
  ).supportedEntryTypes;
  if (!Array.isArray(supported)) return true;
  return supported.includes(type);
}

export function trackWebVitalsPostHog() {
  if (!isPostHogConfigured() || typeof PerformanceObserver === "undefined") return;

  const capture = (name: string, value: number, extras?: Record<string, unknown>) => {
    posthog.capture("web_vital", {
      metric_name: name,
      metric_value: Math.round(name === "CLS" ? value * 1000 : value),
      metric_unit: name === "CLS" ? "score_x1000" : "ms",
      page_path: window.location.pathname,
      ...extras,
    });
  };

  // LCP – capture the last (largest) candidate on page hide, not on every update
  try {
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    let lastLcp: any = null;
    const lcpObserver = new PerformanceObserver((list) => {
      const entries = list.getEntries();
      lastLcp = entries[entries.length - 1];
    });
    lcpObserver.observe({ type: "largest-contentful-paint", buffered: true });

    document.addEventListener("visibilitychange", () => {
      if (document.visibilityState === "hidden" && lastLcp) {
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        capture("LCP", (lastLcp as any).startTime, {
          element_tag: (lastLcp as { element?: Element }).element?.tagName,
        });
        lcpObserver.disconnect();
      }
    }, { once: true });
  } catch { /* unsupported */ }

  // FCP – fires once, safe to capture immediately
  try {
    const fcpObserver = new PerformanceObserver((list) => {
      for (const entry of list.getEntries()) {
        if (entry.name === "first-contentful-paint") {
          capture("FCP", entry.startTime);
          fcpObserver.disconnect();
        }
      }
    });
    fcpObserver.observe({ type: "paint", buffered: true });
  } catch { /* unsupported */ }

  // CLS – accumulate shifts, capture final value on page hide
  // Firefox (y otros) pueden registrar "Ignoring incompatible entryTypes: layout-shift" si no hay soporte.
  try {
    if (!performanceObserverSupportsEntryType("layout-shift")) {
      /* skip — evita ruido en consola */
    } else {
      let clsValue = 0;
      const clsObserver = new PerformanceObserver((list) => {
        for (const entry of list.getEntries()) {
          // eslint-disable-next-line @typescript-eslint/no-explicit-any
          if (!(entry as any).hadRecentInput) {
            // eslint-disable-next-line @typescript-eslint/no-explicit-any
            clsValue += (entry as any).value;
          }
        }
      });
      clsObserver.observe({ type: "layout-shift", buffered: true });

      document.addEventListener(
        "visibilitychange",
        () => {
          if (document.visibilityState === "hidden") {
            if (clsValue > 0) capture("CLS", clsValue);
            clsObserver.disconnect();
          }
        },
        { once: true },
      );
    }
  } catch { /* unsupported */ }

  // TTFB + DOM timings – available immediately from navigation entry
  try {
    const navObserver = new PerformanceObserver((list) => {
      for (const entry of list.getEntries()) {
        const nav = entry as PerformanceNavigationTiming;
        if (nav.responseStart > 0) {
          capture("TTFB", nav.responseStart - nav.requestStart);
          capture("DOM_Interactive", nav.domInteractive);
          capture("DOM_Complete", nav.domComplete);
          navObserver.disconnect();
        }
      }
    });
    navObserver.observe({ type: "navigation", buffered: true });
  } catch { /* unsupported */ }

  // INP – Interaction to Next Paint; track worst interaction per session
  try {
    let worstInp = 0;
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    let worstInpEntry: any = null;
    const inpObserver = new PerformanceObserver((list) => {
      for (const entry of list.getEntries()) {
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        const e = entry as any;
        if (e.duration > worstInp) {
          worstInp = e.duration;
          worstInpEntry = e;
        }
      }
    });
    inpObserver.observe({ type: "event", buffered: true, durationThreshold: 40 } as PerformanceObserverInit);

    document.addEventListener("visibilitychange", () => {
      if (document.visibilityState === "hidden" && worstInpEntry) {
        capture("INP", worstInpEntry.duration, {
          interaction_type: worstInpEntry.name,
        });
        inpObserver.disconnect();
      }
    }, { once: true });
  } catch { /* unsupported */ }
}

/**
 * Track button clicks with PostHog.
 * Note: $browser, $os, $screen_*, $viewport_*, $browser_language are
 * auto-added by PostHog SDK to every event — no need to repeat them here.
 * user_region and test_country are registered as super properties.
 */
export function trackButtonClickPostHog(
  buttonText: string,
  buttonId: string,
  buttonType: string,
  service?: string,
  countryCode?: string,
  templateType?: string,
) {
  if (!isPostHogConfigured()) return;

  posthog.capture("button_click", {
    button_text: buttonText,
    button_id: buttonId,
    button_type: buttonType,
    service: service || "general",
    page_path: window.location.pathname,
    page_title: document.title,
    country_code: countryCode || localStorage.getItem("test_country") || null,
    template_type: templateType,
    is_biolink_user: templateType === "BioLinkTemplate",
    timestamp: new Date().toISOString(),
  });

  if (buttonType.includes("modal_open") || buttonType.includes("payment")) {
    posthog.capture("conversion_funnel_step", {
      funnel_step: "modal_open",
      service: service || "general",
      button_text: buttonText,
      country_code: countryCode || localStorage.getItem("test_country") || null,
      template_type: templateType,
    });
  }

  if (buttonType.includes("tally_form")) {
    posthog.capture("form_start", {
      form_type: "tally",
      form_id: buttonId.includes("tally") ? "contact_form" : "unknown",
      button_text: buttonText,
      page_path: window.location.pathname,
    });
  }
}

/**
 * Track payment modal interactions with PostHog
 */
export function trackPaymentModalPostHog(
  action: "open" | "close" | "method_select",
  service: string,
  method?: string,
) {
  if (!isPostHogConfigured()) return;

  posthog.capture("payment_modal_interaction", {
    action: action,
    service: service,
    payment_method: method || "unknown",
    page_path: window.location.pathname,
    timestamp: new Date().toISOString(),
    user_region: getUserRegionForTracking(),
  });

  // Track conversion funnel
  if (action === "method_select") {
    posthog.capture("conversion_funnel_step", {
      event_category: "Conversion",
      event_label: `${service} - Payment Method Selected`,
      funnel_step: "payment_method_selected",
      service: service,
      payment_method: method,
      country_code: localStorage.getItem("test_country") || "LATAM",
    });
  }
}

/**
 * Track template/layout usage with PostHog.
 * browser_language, timezone, is_mobile are auto-captured by PostHog SDK.
 */
export function trackTemplateUsagePostHog(
  template: string,
  region: string,
  features: string[],
) {
  if (!isPostHogConfigured()) return;

  posthog.capture("template_usage", {
    template,
    region,
    features,
    page_path: window.location.pathname,
    page_title: document.title,
    timestamp: new Date().toISOString(),
  });
}

/**
 * Track Best Friend Instagram (Close Friends) click (membership subscription)
 * For conversion and revenue attribution
 */
export function trackBestFriendInstagramPostHog() {
  if (!isPostHogConfigured()) return;

  posthog.capture("bestfriend_instagram_click", {
    revenue: 6999,
    currency: "ARS",
    service: "bestfriend_instagram",
    page_path: window.location.pathname,
    template: "BaseTemplate",
    checkout_url: "/bestfriend-instagram",
    timestamp: new Date().toISOString(),
    user_region: getUserRegionForTracking(),
  });
}

/**
 * Track social media link clicks with PostHog
 */
export function trackSocialClickPostHog(platform: string, linkUrl: string) {
  if (!isPostHogConfigured()) return;

  posthog.capture("social_click", {
    platform: platform.toLowerCase(),
    link_url: linkUrl,
    page_path: window.location.pathname,
    timestamp: new Date().toISOString(),
    user_region: getUserRegionForTracking(),
  });
}

/**
 * Track photo interactions with PostHog
 */
export function trackPhotoInteractionPostHog(
  action: "click" | "view" | "lightbox_open" | "lightbox_close",
  photoId: string | number,
  photoTitle?: string,
  pagePath?: string,
) {
  if (!isPostHogConfigured()) return;

  posthog.capture("photo_interaction", {
    action: action,
    photo_id: String(photoId),
    photo_title: photoTitle || `Photo ${photoId}`,
    page_path: pagePath || window.location.pathname,
    timestamp: new Date().toISOString(),
    template_type:
      getUserRegionForTracking() === "US"
        ? "BioLinkTemplate"
        : "BaseTemplate",
  });
}

// ─── Cloudinary image engagement (portfolio "what / why") ─────────────────────

/** Thumbnail impression (entró al viewport). Denominador para el CTR por imagen. */
export function trackCloudinaryImageImpressionPostHog(props: {
  publicId: string;
  section?: string;
  pagePath?: string;
  firstSeenOrder?: number;
}) {
  if (!isPostHogConfigured()) return;
  posthog.capture("cloudinary_image_impression", {
    public_id: props.publicId,
    section: props.section,
    page_path: props.pagePath ?? window.location.pathname,
    first_seen_order: props.firstSeenOrder,
  });
}

/** Apertura/tap/zoom de una imagen. Numerador del CTR + señal de interés. */
export function trackCloudinaryImageOpenPostHog(props: {
  publicId: string;
  section?: string;
  altText?: string;
  interactionType: string;
  viewIndex: number;
  sessionImageOpens: number;
  openOrder?: number;
  firstSeenOrder?: number;
  pagePath?: string;
  template?: string;
}) {
  if (!isPostHogConfigured()) return;
  const imageName = props.publicId.split("/").pop() || props.publicId;
  posthog.capture("cloudinary_image_open", {
    public_id: props.publicId,
    image_name: imageName,
    section: props.section,
    alt_text: props.altText,
    interaction_type: props.interactionType,
    view_index: props.viewIndex,
    session_image_opens: props.sessionImageOpens,
    open_order: props.openOrder,
    first_seen_order: props.firstSeenOrder,
    page_path: props.pagePath ?? window.location.pathname,
    template: props.template,
  });
}

/** Resumen al final de la sesión de fotos: qué vieron, cuánto, en qué orden y por qué. */
export function trackCloudinaryImageSessionPostHog(props: {
  photoCount: number;
  totalImpressions: number;
  totalOpens: number;
  totalZooms: number;
  totalDwellMs: number;
  durationMs: number;
  topPublicId?: string;
  topByCtrPublicId?: string;
  exitPublicId?: string;
  sequence?: string;
  source?: string;
  referrer?: string;
  pagePath?: string;
  template?: string;
}) {
  if (!isPostHogConfigured()) return;
  posthog.capture("cloudinary_image_session", {
    photo_count: props.photoCount,
    total_impressions: props.totalImpressions,
    total_opens: props.totalOpens,
    total_zooms: props.totalZooms,
    total_dwell_ms: props.totalDwellMs,
    duration_ms: props.durationMs,
    top_public_id: props.topPublicId,
    top_by_ctr_public_id: props.topByCtrPublicId,
    exit_public_id: props.exitPublicId,
    sequence: props.sequence,
    source: props.source,
    referrer: props.referrer,
    page_path: props.pagePath ?? window.location.pathname,
    template: props.template,
  });
}

/**
 * Track video/reel interactions with PostHog
 */
export function trackVideoInteractionPostHog(
  action: "click" | "play" | "pause" | "complete",
  videoId: string | number,
  videoTitle?: string,
  embedId?: string,
  pagePath?: string,
) {
  if (!isPostHogConfigured()) return;

  posthog.capture("video_interaction", {
    action: action,
    video_id: String(videoId),
    video_title: videoTitle || `Video ${videoId}`,
    embed_id: embedId,
    page_path: pagePath || window.location.pathname,
    timestamp: new Date().toISOString(),
    user_region: getUserRegionForTracking(),
  });
}

/**
 * Track internal link clicks with PostHog
 */
export function trackLinkClickPostHog(linkText: string, linkUrl: string) {
  if (!isPostHogConfigured()) return;

  posthog.capture("link_click", {
    link_text: linkText,
    link_url: linkUrl,
    page_path: window.location.pathname,
    timestamp: new Date().toISOString(),
    is_internal:
      linkUrl.startsWith("/") || linkUrl.includes(window.location.hostname),
  });
}

/**
 * Track page views with enhanced data.
 * When on "/", pass template so session replays can be filtered by template.
 *
 * Note: PostHog JS auto-adds to every event: $current_url, $browser, $os,
 * $screen_*, $viewport_*, $referrer, $browser_language, $device_type, GeoIP.
 * We only include custom properties not captured by the SDK.
 */
export function trackPageViewPostHog(
  pagePath?: string,
  pageTitle?: string,
  template?: string | null,
) {
  if (!isPostHogConfigured()) return;

  const payload: Record<string, unknown> = {
    // Standard $pageview properties PostHog expects
    $current_url: window.location.href,
    // Custom properties — not auto-captured by PostHog JS
    path: pagePath || window.location.pathname,
    title: pageTitle || document.title,
    hash: window.location.hash,
    user_region: getUserRegionForTracking(),
    test_country: localStorage.getItem("test_country"),
    timestamp: new Date().toISOString(),
    // Browser timezone (more precise than GeoIP tz for local-time analysis)
    browser_timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
  };
  if (template) payload.template = template;

  posthog.capture("$pageview", payload);
}

/** IDs para enlazar Person / Session / replay en PostHog desde Slack (best-effort). */
export type PostHogCorrelationIds = {
  distinct_id: string | null;
  session_id: string | null;
  session_replay_url: string | null;
  session_report_url: string | null;
};

export function getPostHogCorrelationIdsPostHog(options?: {
  sessionReplay?: { withTimestamp?: boolean; timestampLookBack?: number };
}): PostHogCorrelationIds {
  if (!isPostHogConfigured()) {
    return {
      distinct_id: null,
      session_id: null,
      session_replay_url: null,
      session_report_url: null,
    };
  }
  try {
    const distinct_id = posthog.get_distinct_id?.() ?? null;
    const sidRaw = posthog.get_session_id?.() ?? "";
    const session_id = sidRaw.length > 0 ? sidRaw : null;
    const sr = options?.sessionReplay;
    const replayOpts =
      sr &&
      (sr.withTimestamp === true || sr.timestampLookBack != null)
        ? {
            ...(sr.withTimestamp === true ? { withTimestamp: true as const } : {}),
            ...(sr.timestampLookBack != null
              ? { timestampLookBack: sr.timestampLookBack }
              : {}),
          }
        : undefined;
    const session_replay_url =
      typeof posthog.get_session_replay_url === "function"
        ? posthog.get_session_replay_url(replayOpts) || null
        : null;
    // Real app UI replay link (project 445055). Never /report/?token=… (CSP endpoint).
    const session_report_url =
      session_replay_url ||
      buildPostHogSessionReportUrl({
        sessionId: session_id,
        distinctId: distinct_id,
        projectId:
          import.meta.env.VITE_POSTHOG_PROJECT_ID ||
          import.meta.env.POSTHOG_PROJECT_ID ||
          "445055",
        host: "https://us.posthog.com",
      });
    return { distinct_id, session_id, session_replay_url, session_report_url };
  } catch {
    return {
      distinct_id: null,
      session_id: null,
      session_replay_url: null,
      session_report_url: null,
    };
  }
}

/**
 * Props listas para `capture()` y para `/api/track-funnel-failure` → Slack:
 * `ph_session_id`, `ph_distinct_id`, `ph_session_replay_url`, `ph_session_report_url`,
 * `slack_session_context` (una línea).
 */
export function getPostHogSlackSessionPropertiesPostHog(options?: {
  /** Enlazar replay cerca del momento actual (útil en CTAs). */
  replayNearNow?: boolean;
}): Record<string, string> {
  const replayOpts =
    options?.replayNearNow === true
      ? { sessionReplay: { withTimestamp: true as const, timestampLookBack: 45 } }
      : undefined;
  const c = getPostHogCorrelationIdsPostHog(replayOpts);
  const out: Record<string, string> = {};
  if (c.session_id) out.ph_session_id = c.session_id;
  if (c.distinct_id) out.ph_distinct_id = c.distinct_id;
  if (c.session_replay_url) out.ph_session_replay_url = c.session_replay_url;
  if (c.session_report_url) out.ph_session_report_url = c.session_report_url;

  const bits: string[] = [];
  if (c.session_id) bits.push(`Session ${c.session_id}`);
  if (c.distinct_id) bits.push(`Person ${c.distinct_id}`);
  if (c.session_replay_url) bits.push(`Replay: ${c.session_replay_url}`);
  else if (c.session_report_url) bits.push(`Replay: ${c.session_report_url}`);
  if (bits.length) out.slack_session_context = bits.join(" · ");

  return out;
}

/**
 * Rich client context for custom events (viewport, first landing URL, motion prefs).
 * Keeps values bounded; use {@link getUrlQueryKeysPostHog} instead of full query strings.
 */
export function getClientContextPropertiesPostHog(): Record<string, unknown> {
  if (typeof window === "undefined") return {};

  let entryLandingUrl: string | null = null;
  try {
    entryLandingUrl = sessionStorage.getItem("entry_page_url");
  } catch {
    /* */
  }

  const reducedMotion =
    window.matchMedia?.("(prefers-reduced-motion: reduce)")?.matches === true;

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const nav = navigator as any;
  const deviceMemory =
    typeof nav.deviceMemory === "number" ? nav.deviceMemory : null;

  return {
    viewport_width: window.innerWidth,
    viewport_height: window.innerHeight,
    screen_width: window.screen?.width ?? null,
    screen_height: window.screen?.height ?? null,
    device_pixel_ratio:
      typeof window.devicePixelRatio === "number"
        ? Math.round(window.devicePixelRatio * 100) / 100
        : null,
    entry_landing_url: entryLandingUrl ? entryLandingUrl.slice(0, 500) : null,
    browser_timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
    prefers_reduced_motion: reducedMotion,
    device_memory_gb: deviceMemory,
    document_visibility: document.visibilityState,
  };
}

/**
 * Explicit PostHog IDs on high-value events — warehouse joins when `$session_id` parsing differs.
 */
export function getPostHogIdentityPropertiesPostHog(): Record<string, string> {
  const c = getPostHogCorrelationIdsPostHog();
  const out: Record<string, string> = {};
  if (c.session_id) out.ph_client_session_id = c.session_id;
  if (c.distinct_id) out.ph_client_distinct_id = c.distinct_id;
  return out;
}

/** Query param *names* (sorted, comma-separated) — no values, no PII. */
export function getUrlQueryKeysPostHog(search: string): string | null {
  try {
    const keys = [...new URLSearchParams(search).keys()];
    if (keys.length === 0) return null;
    return [...new Set(keys)].sort().join(",");
  } catch {
    return null;
  }
}

/**
 * Flat properties for `/api/track-funnel-failure` → Slack: PostHog correlation + client context.
 * Values are only string | number | boolean (API sanitizer).
 */
export function getSlackFunnelDiagnosticsExtraPostHog(options?: {
  replayNearNow?: boolean;
}): Record<string, string | number | boolean> {
  const slack = getPostHogSlackSessionPropertiesPostHog(
    options?.replayNearNow ? { replayNearNow: true } : undefined,
  );
  const out: Record<string, string | number | boolean> = { ...slack };
  Object.assign(out, getPostHogIdentityPropertiesPostHog());

  if (typeof window === "undefined") return out;

  const ctx = getClientContextPropertiesPostHog();
  if (typeof ctx.viewport_width === "number") out.viewport_w = ctx.viewport_width;
  if (typeof ctx.viewport_height === "number") out.viewport_h = ctx.viewport_height;
  if (typeof ctx.device_pixel_ratio === "number") out.dpr = ctx.device_pixel_ratio;
  if (typeof ctx.entry_landing_url === "string" && ctx.entry_landing_url)
    out.entry_landing_url = ctx.entry_landing_url.slice(0, 500);
  if (typeof ctx.browser_timezone === "string") out.browser_tz = ctx.browser_timezone;
  if (ctx.prefers_reduced_motion === true) out.prefers_reduced_motion = true;
  if (typeof ctx.device_memory_gb === "number") out.device_memory_gb = ctx.device_memory_gb;
  if (typeof ctx.document_visibility === "string")
    out.document_visibility = ctx.document_visibility;

  const qk = getUrlQueryKeysPostHog(window.location.search);
  if (qk) out.url_query_keys = qk;

  // HeroCTA + template + geo del worker snapshot — admin ve qué CTA estaba
  // viendo el visitor al disparar el evento, sin tener que cross-referenciar
  // con admin DB. Spreadeado al final para no pisar viewport/replay/etc.,
  // pero al inicio del extra del payload (los callsites individuales pueden
  // overridear keys si necesitan).
  const heroExtra = getHeroCtaSlackExtra();
  for (const [k, v] of Object.entries(heroExtra)) {
    if (v !== undefined && v !== "") out[k] = v;
  }

  return out;
}

/**
 * IDs PostHog cortos para metadata de Stripe (límite ~500 chars/valor; no incluir replay URL).
 * Permite que webhooks (`payment_completed`) enlacen el Slack con la sesión del cliente.
 */
export function getPostHogStripeMetadataPostHog(): Record<string, string> {
  const c = getPostHogCorrelationIdsPostHog();
  const out: Record<string, string> = {};
  if (c.session_id) out.ph_session_id = c.session_id.slice(0, 128);
  if (c.distinct_id) out.ph_distinct_id = c.distinct_id.slice(0, 256);
  return out;
}

/** Clave sessionStorage — una ID estable por pestaña para correlacionar clicks BaseTemplate. */
export const POSTHOG_BASE_TEMPLATE_TAB_SESSION_KEY = "base_template_tab_session_id_v1";

export function getOrCreateTabSessionId(storageKey: string): string {
  if (typeof window === "undefined") return "";
  try {
    let id = sessionStorage.getItem(storageKey);
    if (!id?.trim()) {
      id =
        typeof crypto !== "undefined" && "randomUUID" in crypto
          ? crypto.randomUUID()
          : `ts_${Date.now()}_${Math.random().toString(36).slice(2, 10)}`;
      sessionStorage.setItem(storageKey, id);
    }
    return id;
  } catch {
    return `ts_${Date.now()}`;
  }
}

const PREMIUM_INVITE_FUNNEL_REG_KEYS = [
  "pi_invite_session_id",
  "pi_funnel_currency",
  "pi_language_base",
  "pi_special_offer_active",
  "pi_has_vip_mux",
  "pi_vip_plan_card_enabled",
  "pi_snapshot_version",
  "pi_shop_region",
  "pi_stripe_intl_currency",
  "pi_telegram_basic_gateway",
  "ph_session_id",
  "ph_distinct_id",
  "ph_session_replay_url",
  "ph_session_report_url",
  "slack_session_context",
] as const;

export type PremiumInviteFunnelRegisterMetaPostHog = {
  shop_region: "AR" | "intl";
  /** Solo aplica cuando shop_region === intl (EUR vs USD Stripe). */
  stripe_intl_currency?: "USD";
  /** Pasarela prevista para el plan grupo/basic. */
  telegram_basic_intended_gateway: "mercadopago" | "stripe";
};

/**
 * Super-properties mientras el visitante está en BioTemplatePremiumSimple (cualquier ruta).
 * Todo `captureEvent` hereda invite session + snapshot + PostHog replay linkage.
 */
export function registerPremiumInviteFunnelContextPostHog(
  inviteSessionId: string,
  snapshot: PremiumPlansFunnelSnapshot,
  funnelMeta?: PremiumInviteFunnelRegisterMetaPostHog,
): void {
  if (!isPostHogConfigured()) return;
  const slack = getPostHogSlackSessionPropertiesPostHog();
  posthog.register({
    pi_invite_session_id: inviteSessionId,
    pi_funnel_currency: snapshot.currency,
    pi_language_base: snapshot.language_base,
    pi_special_offer_active: snapshot.special_offer_active,
    pi_has_vip_mux: snapshot.has_vip_mux,
    pi_vip_plan_card_enabled: snapshot.vip_plan_card_enabled,
    pi_snapshot_version: snapshot.snapshot_version,
    ...(funnelMeta
      ? {
          pi_shop_region: funnelMeta.shop_region,
          ...(funnelMeta.stripe_intl_currency
            ? { pi_stripe_intl_currency: funnelMeta.stripe_intl_currency }
            : {}),
          pi_telegram_basic_gateway: funnelMeta.telegram_basic_intended_gateway,
        }
      : {}),
    ...slack,
  });
}

export function unregisterPremiumInviteFunnelContextPostHog(): void {
  if (!isPostHogConfigured()) return;
  try {
    (posthog as unknown as { unregister?: (keys: string[]) => void }).unregister?.([
      ...PREMIUM_INVITE_FUNNEL_REG_KEYS,
    ]);
  } catch {
    /* */
  }
}

/**
 * Evento único por carga — usar en funnels “Session started” y para ver riesgos (ej. sin Mux).
 */
export function trackPremiumInviteFunnelSessionPostHog(
  inviteSessionId: string,
  snapshot: PremiumPlansFunnelSnapshot,
  context: {
    country_code: string;
    region: string;
    funnel_risk_flags?: string[];
    shop_region: "AR" | "intl";
    stripe_intl_currency?: "USD";
    telegram_basic_intended_gateway: "mercadopago" | "stripe";
  },
): void {
  if (!isPostHogConfigured()) return;
  const slack = getPostHogSlackSessionPropertiesPostHog();
  const geo = getWorkerGeoAnalyticsProps();
  const geoFlat = Object.fromEntries(
    Object.entries(geo).filter(([, v]) => v !== undefined && v !== ""),
  ) as Record<string, string>;

  posthog.capture("premium_invite_funnel_session", {
    invite_session_id: inviteSessionId,
    ...snapshot,
    country_code: context.country_code,
    region: context.region,
    shop_region: context.shop_region,
    ...(context.stripe_intl_currency
      ? { stripe_intl_currency: context.stripe_intl_currency }
      : {}),
    telegram_basic_intended_gateway: context.telegram_basic_intended_gateway,
    page_path: window.location.pathname,
    page_url: window.location.href,
    funnel_risk_flags: context.funnel_risk_flags,
    user_region: getUserRegionForTracking(),
    timestamp: new Date().toISOString(),
    ...geoFlat,
    ...slack,
  });
}

const BASE_TEMPLATE_REG_KEYS = [
  "base_tab_session_id",
  "base_template_surface",
  "base_country_worker",
  "base_instagram_in_app",
] as const;

const BASE_TEMPLATE_GEO_AND_PH_KEYS = [
  "ph_session_id",
  "ph_distinct_id",
  "ph_session_replay_url",
  "ph_session_report_url",
  "slack_session_context",
  "worker_country",
  "worker_cf_country",
  "worker_province",
  "worker_city",
  "worker_timezone",
  "worker_detection_method",
  "geo_summary",
] as const;

export function registerBaseTemplateSessionContextPostHog(
  tabSessionId: string,
  opts: {
    templateSurface: string;
    countryCode?: string;
    isInstagramInApp: boolean;
  },
): void {
  if (!isPostHogConfigured()) return;
  const slack = getPostHogSlackSessionPropertiesPostHog();
  const geo = getWorkerGeoAnalyticsProps();
  const geoFlat = Object.fromEntries(
    Object.entries(geo).filter(([, v]) => v !== undefined && v !== ""),
  ) as Record<string, string>;
  posthog.register({
    base_tab_session_id: tabSessionId,
    base_template_surface: opts.templateSurface,
    base_country_worker: opts.countryCode || undefined,
    base_instagram_in_app: opts.isInstagramInApp,
    ...slack,
    ...geoFlat,
  });
}

export function unregisterBaseTemplateSessionContextPostHog(): void {
  if (!isPostHogConfigured()) return;
  try {
    (posthog as unknown as { unregister?: (keys: string[]) => void }).unregister?.([
      ...BASE_TEMPLATE_REG_KEYS,
      ...BASE_TEMPLATE_GEO_AND_PH_KEYS,
    ]);
  } catch {
    /* */
  }
}

export function trackBaseTemplateFunnelSessionPostHog(args: {
  tabSessionId: string;
  templateSurface: string;
  countryCode?: string;
  isInstagramInApp: boolean;
  features: string[];
}): void {
  if (!isPostHogConfigured()) return;
  const slack = getPostHogSlackSessionPropertiesPostHog();
  const geo = getWorkerGeoAnalyticsProps();
  const geoFlat = Object.fromEntries(
    Object.entries(geo).filter(([, v]) => v !== undefined && v !== ""),
  ) as Record<string, string>;

  posthog.capture("base_template_funnel_session", {
    base_tab_session_id: args.tabSessionId,
    template_surface: args.templateSurface,
    country_code: args.countryCode,
    is_instagram_in_app: args.isInstagramInApp,
    features: args.features,
    page_path: window.location.pathname,
    page_url: window.location.href,
    user_region: getUserRegionForTracking(),
    timestamp: new Date().toISOString(),
    ...geoFlat,
    ...slack,
  });
}

const BIO_PREMIUM_REGISTER_KEYS = [
  "bio_premium_flow_trace_id",
  "bio_premium_tab_session_id",
  "template_funnel",
] as const;

/**
 * Super-properties en todos los eventos mientras el visitante está en BioTemplatePremium
 * — correlaciona clics/checkout con Slack y Stripe `flow_trace_id`.
 */
export function registerBioPremiumFunnelContextPostHog(
  flowTraceId: string,
  tabSessionId: string,
): void {
  if (!isPostHogConfigured()) return;
  posthog.register({
    bio_premium_flow_trace_id: flowTraceId,
    bio_premium_tab_session_id: tabSessionId,
    template_funnel: "BioTemplatePremium",
  });
}

export function unregisterBioPremiumFunnelContextPostHog(): void {
  if (!isPostHogConfigured()) return;
  try {
    (posthog as unknown as { unregister?: (keys: string[]) => void }).unregister?.([
      ...BIO_PREMIUM_REGISTER_KEYS,
    ]);
  } catch {
    /* */
  }
}

/** Inicio explícito de sesión funnel (pricing + país) — usar en dashboards y Slack linkage. */
export function trackBioPremiumFunnelSessionStartedPostHog(args: {
  flowName: string;
  flowTraceId: string;
  tabSessionId: string;
  country: string;
  currency: "ARS" | "USD";
  language: string;
  provider: "mercadopago" | "stripe";
  snapshot: PremiumPlansFunnelSnapshot;
  standard_plan: { price: number; productName: string };
  vip_plan?: { price: number; productName: string; isDisabled?: boolean };
}): void {
  if (!isPostHogConfigured()) return;

  const slack = getPostHogSlackSessionPropertiesPostHog();

  posthog.capture("bio_premium_funnel_session_started", {
    template_type: "BioTemplatePremium",
    flow_name: args.flowName,
    flow_trace_id: args.flowTraceId,
    tab_session_id: args.tabSessionId,
    country_code: args.country,
    gateway_provider: args.provider,
    locale_full: args.language,
    standard_price_live: args.standard_plan.price,
    standard_product_name: args.standard_plan.productName,
    vip_price_live: args.vip_plan?.price ?? null,
    vip_product_name: args.vip_plan?.productName ?? null,
    vip_card_disabled: Boolean(args.vip_plan?.isDisabled),
    ...slack,
    page_path: window.location.pathname,
    page_url: window.location.href,
    user_region: getUserRegionForTracking(),
    timestamp: new Date().toISOString(),
    ...args.snapshot,
  });

  posthog.capture("conversion_funnel_step", {
    funnel_step: "bio_premium_session_started",
    template_type: "BioTemplatePremium",
    flow_name: args.flowName,
    flow_trace_id: args.flowTraceId,
    tab_session_id: args.tabSessionId,
    country_code: args.country,
    currency: args.currency,
    gateway_provider: args.provider,
  });
}

/** Tiempo total en landing premium — disparar en `pagehide`/unmount (evita pérdidas en SPA). */
export function trackPremiumFunnelTimeOnPagePostHog(params: {
  template_type: "BioTemplatePremium" | "BioTemplatePremiumSimple";
  time_on_page_seconds: number;
  flow_name?: string;
  flow_trace_id?: string;
  tab_session_id?: string;
  session_id?: string;
  country?: string;
}) {
  if (!isPostHogConfigured()) return;

  posthog.capture("premium_funnel_time_on_page", {
    ...params,
    page_path: typeof window !== "undefined" ? window.location.pathname : null,
    page_url: typeof window !== "undefined" ? window.location.href : null,
    user_region:
      typeof window !== "undefined"
        ? getUserRegionForTracking()
        : null,
    timestamp: new Date().toISOString(),
  });
}

/**
 * Resumen de engagement con el video VIP (max posición vista vs duración del asset).
 * `watch_seconds_max` suele provenir del máximo `currentTime` durante el modal (Mux telemetry).
 */
export function trackPremiumVipVideoWatchSummaryPostHog(params: {
  template_type: "BioTemplatePremium" | "BioTemplatePremiumSimple";
  flow_name?: string;
  flow_trace_id?: string;
  tab_session_id?: string;
  session_id?: string;
  country?: string;
  playback_id?: string;
  currency?: string;
  exit_reason: "modal_close" | "cta_click" | "ended";
  watch_seconds_max: number;
  video_duration_seconds: number | null;
}) {
  if (!isPostHogConfigured()) return;

  const dur = params.video_duration_seconds;
  const capped =
    dur != null && dur > 0
      ? Math.min(Math.max(0, params.watch_seconds_max), dur)
      : Math.max(0, params.watch_seconds_max);
  const pct =
    dur != null && dur > 0 ? Math.round((capped / dur) * 1000) / 10 : null;

  posthog.capture("premium_vip_video_watch_summary", {
    template_type: params.template_type,
    flow_name: params.flow_name,
    flow_trace_id: params.flow_trace_id,
    tab_session_id: params.tab_session_id,
    session_id: params.session_id,
    country_code: params.country,
    playback_id: params.playback_id,
    currency: params.currency,
    exit_reason: params.exit_reason,
    watch_seconds_max: Math.round(params.watch_seconds_max * 100) / 100,
    video_duration_seconds: dur,
    watch_seconds_capped: Math.round(capped * 100) / 100,
    percent_complete_estimate: pct,
    page_path: typeof window !== "undefined" ? window.location.pathname : null,
    user_region:
      typeof window !== "undefined"
        ? getUserRegionForTracking()
        : null,
    timestamp: new Date().toISOString(),
  });
}

/**
 * Vista del embudo premium simple (BioTemplatePremiumSimple).
 * Se dispara en **cualquier** ruta donde monte el template (p. ej. `/` por Worker o `/invitacion-premium`).
 */
export function trackPremiumInvitePageViewPostHog(params: {
  country: string;
  language: string;
  session_id?: string;
  email_prefilled?: boolean;
  recovery_source?: "recovery_email";
  /** Precios/planMux — correlación con Slack y funnel bio premium */
  plans_funnel_snapshot?: PremiumPlansFunnelSnapshot;
  /** Canal Telegram público gratuito (VITE_PREMIUM_SIMPLE_FREE_TELEGRAM_URL) visible en la página */
  has_free_telegram_url?: boolean;
}) {
  if (!isPostHogConfigured() || typeof window === "undefined") return;

  const pagePath =
    window.location.pathname.replace(/\/$/, "") || "/";
  const dedicatedInviteRoute =
    pagePath === "/invitacion-premium" ||
    pagePath === "/invitacion" ||
    pagePath === "/premium-invite";
  const invite_surface = dedicatedInviteRoute
    ? "dedicated_route"
    : "worker_bio_home";
  const shopRegion =
    String(params.country ?? "")
      .trim()
      .toUpperCase() === "AR"
      ? "AR"
      : "intl";

  const slack = getPostHogSlackSessionPropertiesPostHog();

  posthog.capture("premium_invite_page_view", {
    page_path: pagePath,
    invite_surface,
    shop_region: shopRegion,
    $current_url: window.location.href,
    country: params.country,
    language: params.language,
    session_id: params.session_id || null,
    email_prefilled: params.email_prefilled ?? false,
    recovery_source: params.recovery_source ?? null,
    has_free_telegram_url: params.has_free_telegram_url ?? false,
    ...(params.plans_funnel_snapshot ?? {}),
    ...slack,
    ...parseUTMFromSearch(window.location.search),
    user_region: getUserRegionForTracking(),
    test_country: localStorage.getItem("test_country"),
    timestamp: new Date().toISOString(),
  });
}

/**
 * Track invitacion-mejores-amigos (Best Friends Instagram) / grupo-privado (Telegram Groups) page events (view, CTA click, close).
 * Use page_path in props to distinguish routes; defaults to /invitacion-mejores-amigos.
 */
export function trackInvitacionGrupoPostHog(
  action: "view" | "cta_click" | "close",
  props?: {
    page_path?: string;
    cta_url?: string;
    protection_violation?: string;
    [key: string]: any; // Allow additional properties for protection tracking
  },
) {
  if (!isPostHogConfigured()) return;

  const pagePath =
    props?.page_path ??
    (props as { current_path?: string })?.current_path ??
    "/invitacion-mejores-amigos";

  posthog.capture("invitacion_mejores_amigos", {
    ...props,
    action,
    page_path: pagePath,
    page_url: window.location.href,
    referrer: document.referrer,
    user_region: getUserRegionForTracking(),
    timestamp: new Date().toISOString(),
  });
}

// ─── Country SEO landing pages (CountryLanding.tsx) ───────────────────────────

/**
 * View of a data-driven country SEO landing (`src/pages/CountryLanding.tsx`).
 * Fire once on mount. `slug` + `country` are the breakdown dimensions that let
 * us measure which landings/markets convert (the component header long promised
 * this event but no capture existed — this closes that gap).
 */
export function trackCountryLandingViewPostHog(props: {
  slug: string;
  country: string;
  locale?: string;
  ctaPath?: string;
}) {
  if (!isPostHogConfigured()) return;

  posthog.capture("country_landing_view", {
    slug: props.slug,
    country: props.country,
    locale: props.locale ?? null,
    cta_path: props.ctaPath ?? null,
    page_path: window.location.pathname,
    page_url: window.location.href,
    referrer: document.referrer || null,
    user_region: getUserRegionForTracking(),
    timestamp: new Date().toISOString(),
  });
}

/**
 * CTA click on a country SEO landing — the conversion numerator paired with
 * {@link trackCountryLandingViewPostHog}'s denominator. Same `slug`/`country`
 * dimensions so funnels (view → cta_click) break down per landing and market.
 */
export function trackCountryLandingCtaClickPostHog(props: {
  slug: string;
  country: string;
  ctaLabel?: string;
  ctaPath: string;
}) {
  if (!isPostHogConfigured()) return;

  posthog.capture("country_landing_cta_click", {
    slug: props.slug,
    country: props.country,
    cta_label: props.ctaLabel ?? null,
    cta_path: props.ctaPath,
    page_path: window.location.pathname,
    user_region: getUserRegionForTracking(),
    timestamp: new Date().toISOString(),
  });
}

/**
 * Track scroll depth
 */
export function trackScrollDepthPostHog(scrollPercentage: number) {
  if (!isPostHogConfigured()) return;

  posthog.capture("scroll_depth", {
    scroll_percentage: scrollPercentage,
    page_url: window.location.href,
    page_path: window.location.pathname,
    timestamp: new Date().toISOString(),
  });
}

/**
 * Track time spent on page
 */
export function trackTimeOnPagePostHog(timeSpentSeconds: number) {
  if (!isPostHogConfigured()) return;

  posthog.capture("time_on_page", {
    time_spent_seconds: timeSpentSeconds,
    page_url: window.location.href,
    page_path: window.location.pathname,
    timestamp: new Date().toISOString(),
  });
}

/**
 * Track user identification.
 * browser, language, timezone, screen_resolution are auto-captured by PostHog.
 */
export function identifyUserPostHog(
  userId: string,
  properties?: Record<string, any>,
) {
  if (!isPostHogConfigured()) return;

  posthog.identify(userId, {
    ...properties,
    user_region: getUserRegionForTracking(),
    test_country: localStorage.getItem("test_country") || null,
  });
}

/**
 * Set user properties
 */
export function setUserPropertiesPostHog(properties: Record<string, any>) {
  if (!isPostHogConfigured()) return;

  posthog.setPersonProperties({
    ...properties,
    last_updated: new Date().toISOString(),
    user_region: getUserRegionForTracking(),
    test_country: localStorage.getItem("test_country"),
  });
}

/**
 * Track conversion events
 */
export function trackConversionPostHog(
  conversionType: string,
  service: string,
  value?: number,
  currency?: string,
) {
  if (!isPostHogConfigured()) return;

  posthog.capture("conversion", {
    conversion_type: conversionType,
    service: service,
    value: value,
    currency: currency || "USD",
    page_path: window.location.pathname,
    user_region: getUserRegionForTracking(),
    timestamp: new Date().toISOString(),
  });
}

/**
 * Track errors and exceptions.
 * user_agent is auto-captured by PostHog SDK on every event.
 */
export function trackErrorPostHog(error: Error, context?: Record<string, any>) {
  if (!isPostHogConfigured()) return;

  posthog.capture("$exception", {
    $exception_message: error.message,
    $exception_type: error.name,
    $exception_stack_trace_raw: error.stack,
    page_path: window.location.pathname,
    timestamp: new Date().toISOString(),
    ...context,
  });
}

/**
 * Start session recording
 */
export function startSessionRecordingPostHog() {
  if (!isPostHogConfigured()) return;

  try {
    // Override sampling + URL/event triggers (project puede tener gates en remote config).
    posthog.startSessionRecording({
      sampling: true,
      linked_flag: true,
      url_trigger: true,
      event_trigger: true,
    });
  } catch {
    try {
      posthog.startSessionRecording(true);
    } catch {
      /* ignore */
    }
  }
}

/**
 * Stop session recording
 */
export function stopSessionRecordingPostHog() {
  if (!isPostHogConfigured()) return;

  try {
    posthog.stopSessionRecording();
  } catch (error) {}
}

/**
 * Check if session recording is active
 */
export function isSessionRecordingActivePostHog(): boolean {
  if (!isPostHogConfigured()) return false;

  try {
    return posthog.sessionRecordingStarted();
  } catch (error) {
    return false;
  }
}

/**
 * Get session replay URL
 */
export function getSessionReplayUrlPostHog(): string | null {
  if (!isPostHogConfigured()) return null;

  try {
    return posthog.get_session_replay_url();
  } catch (error) {
    return null;
  }
}

/**
 * Track feature flag usage
 */
export function trackFeatureFlagPostHog(flagName: string, flagValue: any) {
  if (!isPostHogConfigured()) return;

  posthog.capture("feature_flag_used", {
    flag_name: flagName,
    flag_value: flagValue,
    page_path: window.location.pathname,
    timestamp: new Date().toISOString(),
  });
}

/**
 * Get feature flag value
 */
export function getFeatureFlagPostHog(flagName: string): any {
  if (!isPostHogConfigured()) return null;

  try {
    return posthog.getFeatureFlag(flagName);
  } catch (error) {
    return null;
  }
}

/**
 * Check if feature is enabled
 */
export function isFeatureEnabledPostHog(featureName: string): boolean {
  if (!isPostHogConfigured()) return false;

  try {
    return posthog.isFeatureEnabled(featureName);
  } catch (error) {
    return false;
  }
}

/**
 * Debug PostHog status
 */
export function debugPostHogStatus() {
  if (typeof window === "undefined") {
    return;
  }
}

/**
 * Track BioTemplatePremium discount offer flow
 */
export function trackDiscountOfferShownPostHog(
  flowName: string,
  planTier: string,
  originalPrice: number,
  discountedPrice: number,
  currency: string,
  country?: string
) {
  if (!isPostHogConfigured()) return;

  posthog.capture("discount_offer_shown", {
    flow_name: flowName,
    plan_tier: planTier,
    original_price: originalPrice,
    discounted_price: discountedPrice,
    discount_percentage: 10,
    currency,
    country_code: country,
    page_path: window.location.pathname,
    timestamp: new Date().toISOString(),
  });
}

export function trackDiscountOfferActionPostHog(
  flowName: string,
  action: "accepted" | "declined",
  planTier: string,
  originalPrice: number,
  discountedPrice: number,
  currency: string,
  country?: string
) {
  if (!isPostHogConfigured()) return;

  posthog.capture("discount_offer_action", {
    flow_name: flowName,
    action,
    plan_tier: planTier,
    original_price: originalPrice,
    discounted_price: discountedPrice,
    discount_percentage: 10,
    currency,
    country_code: country,
    page_path: window.location.pathname,
    timestamp: new Date().toISOString(),
  });
}

export function trackVipVideoOpenedPostHog(
  flowName: string,
  playbackId: string,
  country?: string
) {
  if (!isPostHogConfigured()) return;

  posthog.capture("vip_video_opened", {
    flow_name: flowName,
    playback_id: playbackId.substring(0, 20) + "...",
    country_code: country,
    page_path: window.location.pathname,
    timestamp: new Date().toISOString(),
  });
}

export function trackVipVideoClosedPostHog(
  flowName: string,
  playbackId: string,
  country?: string
) {
  if (!isPostHogConfigured()) return;

  posthog.capture("vip_video_closed", {
    flow_name: flowName,
    playback_id: playbackId.substring(0, 20) + "...",
    country_code: country,
    page_path: window.location.pathname,
    timestamp: new Date().toISOString(),
  });
}

/**
 * Track donation page view
 */
export function trackDonationPageViewPostHog() {
  if (!isPostHogConfigured()) return;

  posthog.capture("donation_page_view", {
    page: "sorprendeme",
    page_path: window.location.pathname,
    timestamp: new Date().toISOString(),
  });
}

/**
 * Track donation amount selection
 */
export function trackDonationAmountSelectedPostHog(
  amount: number,
  isCustom: boolean
) {
  if (!isPostHogConfigured()) return;

  posthog.capture("donation_amount_selected", {
    amount: amount,
    is_custom_amount: isCustom,
    page: "sorprendeme",
    page_path: window.location.pathname,
    timestamp: new Date().toISOString(),
  });
}

/**
 * Track donation form submission
 */
export function trackDonationFormSubmittedPostHog(
  amount: number,
  hasName: boolean,
  messageLength: number
) {
  if (!isPostHogConfigured()) return;

  posthog.capture("donation_form_submitted", {
    amount: amount,
    has_name: hasName,
    message_length: messageLength,
    is_anonymous: !hasName,
    page: "sorprendeme",
    funnel_step: "form_submitted",
    page_path: window.location.pathname,
    timestamp: new Date().toISOString(),
  });
}

/**
 * Track donation payment success
 */
export function trackDonationPaymentSuccessPostHog(
  amount: number,
  donationId: string | null
) {
  if (!isPostHogConfigured()) return;

  posthog.capture("donation_payment_success", {
    amount: amount,
    donation_id: donationId,
    page: "sorprendeme",
    funnel_step: "payment_completed",
    page_path: window.location.pathname,
    timestamp: new Date().toISOString(),
  });

  // También trackear como conversión
  posthog.capture("conversion", {
    conversion_type: "donation",
    conversion_value: amount,
    currency: "ARS",
    page: "sorprendeme",
  });
}

/**
 * Track donation payment error
 */
export function trackDonationPaymentErrorPostHog(
  amount: number,
  errorMessage: string
) {
  if (!isPostHogConfigured()) return;

  posthog.capture("donation_payment_error", {
    amount: amount,
    error_message: errorMessage,
    page: "sorprendeme",
    funnel_step: "payment_failed",
    page_path: window.location.pathname,
    timestamp: new Date().toISOString(),
  });
}

/**
 * Track donation checkout started
 */
export function trackDonationCheckoutStartedPostHog(
  amount: number,
  preferenceId: string
) {
  if (!isPostHogConfigured()) return;

  posthog.capture("donation_checkout_started", {
    amount: amount,
    preference_id: preferenceId,
    page: "sorprendeme",
    funnel_step: "checkout_opened",
    page_path: window.location.pathname,
    timestamp: new Date().toISOString(),
  });
}

// ─── Stripe pay failures (cross-surface: Elements, Checkout Session, PaymentIntent API) ─

const STRIPE_PAY_FAILURE_MSG_MAX = 500;

export type StripePayFailureStage =
  | "stripe_js_unavailable"
  | "payment_intent_create"
  | "checkout_session_create"
  | "payment_confirm"
  | "invalid_config"
  | "flow_blocked"
  | "checkout_redirect_failed"
  | "checkout_flow_error";

/**
 * Use one event for dashboards: breakdown by `stage`, `flow_surface`, `stripe_error_code`, `failure_kind`.
 */
export function trackStripePayFailurePostHog(props: {
  stage: StripePayFailureStage;
  /** e.g. checkout_channel_invite_page, mux_video_cta, bio_template_premium_simple */
  flow_surface: string;
  failure_kind?: string;
  error_message?: string;
  http_status?: number;
  stripe_error_code?: string;
  stripe_error_type?: string;
  decline_code?: string;
  amount_cents?: number;
  currency?: string;
  pack_id?: string;
  plan_tier?: string;
  flow_trace_id?: string;
}) {
  if (!isPostHogConfigured()) return;

  const msg = props.error_message
    ? String(props.error_message).slice(0, STRIPE_PAY_FAILURE_MSG_MAX)
    : undefined;

  posthog.capture("stripe_pay_failure", {
    stage: props.stage,
    flow_surface: props.flow_surface,
    failure_kind: props.failure_kind,
    error_message: msg,
    http_status: props.http_status,
    stripe_error_code: props.stripe_error_code || undefined,
    stripe_error_type: props.stripe_error_type || undefined,
    decline_code: props.decline_code || undefined,
    amount_cents: props.amount_cents,
    currency: props.currency,
    pack_id: props.pack_id,
    plan_tier: props.plan_tier,
    flow_trace_id: props.flow_trace_id,
    page_path: window.location.pathname,
    page_url: window.location.href,
    timestamp: new Date().toISOString(),
  });
}

// ─── Media Gallery Tracking (BioLinkTemplate) ─────────────────────────────────

/**
 * Best-effort extraction of Cloudinary public_id from a full URL.
 * Handles: /upload/v123/folder/name.jpg  →  folder/name
 *          /upload/w_400,f_auto/name.jpg  →  name
 */
function extractCldPublicId(url: string): string | undefined {
  const clean = url.split("?")[0];
  const m = clean.match(/\/upload\/(?:(?:v\d+|[a-z_]+_[^/,]+(?:,[a-z_]+_[^/,]+)*)\/)*(.+?)(?:\.[a-z0-9]+)?$/i);
  return m?.[1];
}

export function trackMediaItemClickPostHog(
  mediaId: string | number,
  mediaType: "video" | "photo",
  mediaTitle?: string,
  positionInGallery?: number,
  templateType?: string,
  countryCode?: string,
  mediaSrc?: string,     // resolved URL (res.cloudinary.com or raw)
) {
  if (!isPostHogConfigured()) return;

  const isCloudinary = !!mediaSrc && mediaSrc.includes("res.cloudinary.com");

  posthog.capture("media_item_click", {
    media_id: String(mediaId),
    media_type: mediaType,
    media_title: mediaTitle || `Media ${mediaId}`,
    position_in_gallery: positionInGallery,
    media_src: mediaSrc || undefined,
    cloudinary_public_id: isCloudinary ? extractCldPublicId(mediaSrc!) : undefined,
    is_cloudinary: isCloudinary,
    template_type: templateType || "BioLinkTemplate",
    country_code: countryCode || localStorage.getItem("test_country") || undefined,
    page_path: window.location.pathname,
    page_url: window.location.href,
    referrer: document.referrer || undefined,
    timestamp: new Date().toISOString(),
    user_region: getUserRegionForTracking(),
  });
}

// ─── Gallery Photo Impression + View-time tracking ────────────────────────────

/**
 * Fired the FIRST time a gallery photo becomes ≥50 % visible in the viewport.
 * One event per photo per page session (deduplication is handled in BaseTemplate).
 */
export function trackGalleryPhotoImpressionPostHog(params: {
  mediaId: string | number;
  mediaSrc: string;
  cloudinaryPublicId?: string;
  positionInGallery: number;
  mediaType: "video" | "photo";
  templateType?: string;
  countryCode?: string;
}) {
  if (!isPostHogConfigured()) return;
  posthog.capture("gallery_photo_impression", {
    media_id: String(params.mediaId),
    media_src: params.mediaSrc,
    cloudinary_public_id: params.cloudinaryPublicId,
    is_cloudinary: params.mediaSrc.includes("res.cloudinary.com"),
    position_in_gallery: params.positionInGallery,
    media_type: params.mediaType,
    template_type: params.templateType ?? "BaseTemplate",
    country_code: params.countryCode ?? localStorage.getItem("test_country") ?? undefined,
    page_path: window.location.pathname,
    page_url: window.location.href,
    referrer: document.referrer || undefined,
    timestamp: new Date().toISOString(),
    user_region: getUserRegionForTracking(),
  });
}

/**
 * Fired when a gallery photo leaves the viewport (or on page hide).
 * Ignored if the photo was visible for < 300 ms (scroll-through).
 */
export function trackGalleryPhotoViewTimePostHog(params: {
  mediaId: string | number;
  mediaSrc: string;
  cloudinaryPublicId?: string;
  positionInGallery: number;
  mediaType: "video" | "photo";
  viewTimeMs: number;
  templateType?: string;
  countryCode?: string;
}) {
  if (!isPostHogConfigured()) return;
  if (params.viewTimeMs < 300) return; // ignore scroll-through flashes
  posthog.capture("gallery_photo_view_time", {
    media_id: String(params.mediaId),
    media_src: params.mediaSrc,
    cloudinary_public_id: params.cloudinaryPublicId,
    is_cloudinary: params.mediaSrc.includes("res.cloudinary.com"),
    position_in_gallery: params.positionInGallery,
    media_type: params.mediaType,
    view_time_ms: Math.round(params.viewTimeMs),
    view_time_seconds: Math.round(params.viewTimeMs / 100) / 10,
    template_type: params.templateType ?? "BaseTemplate",
    country_code: params.countryCode ?? localStorage.getItem("test_country") ?? undefined,
    page_path: window.location.pathname,
    page_url: window.location.href,
    referrer: document.referrer || undefined,
    timestamp: new Date().toISOString(),
    user_region: getUserRegionForTracking(),
  });
}

/**
 * Fired each time the user navigates to the next/prev photo in the lightbox,
 * or when the lightbox is closed — captures dwell time on each individual photo.
 */
export function trackLightboxPhotoDwellPostHog(params: {
  mediaId: string | number;
  mediaSrc?: string;
  cloudinaryPublicId?: string;
  mediaType: "video" | "photo";
  dwellMs: number;
  exitDirection: "prev" | "next" | "close";
  templateType?: string;
  countryCode?: string;
}) {
  if (!isPostHogConfigured()) return;
  if (params.dwellMs < 300) return;
  const isCloudinary = !!params.mediaSrc && params.mediaSrc.includes("res.cloudinary.com");
  posthog.capture("lightbox_photo_dwell", {
    media_id: String(params.mediaId),
    media_src: params.mediaSrc,
    cloudinary_public_id: params.cloudinaryPublicId,
    is_cloudinary: isCloudinary,
    media_type: params.mediaType,
    dwell_ms: Math.round(params.dwellMs),
    dwell_seconds: Math.round(params.dwellMs / 100) / 10,
    exit_direction: params.exitDirection,
    template_type: params.templateType ?? "BaseTemplate",
    country_code: params.countryCode ?? localStorage.getItem("test_country") ?? undefined,
    page_path: window.location.pathname,
    page_url: window.location.href,
    timestamp: new Date().toISOString(),
    user_region: getUserRegionForTracking(),
  });
}

export function trackLightboxOpenedPostHog(
  mediaId: string | number,
  source: "gallery_click" | "direct_link",
  mediaType?: "video" | "photo",
  templateType?: string,
  countryCode?: string
) {
  if (!isPostHogConfigured()) return;

  posthog.capture("lightbox_opened", {
    media_id: String(mediaId),
    source,
    media_type: mediaType,
    template_type: templateType || "BioLinkTemplate",
    country_code: countryCode || localStorage.getItem("test_country") || undefined,
    page_path: window.location.pathname,
    page_url: window.location.href,
    timestamp: new Date().toISOString(),
    user_region: getUserRegionForTracking(),
  });
}

export function trackLightboxClosedPostHog(
  mediaId: string | number,
  timeSpentSeconds: number,
  viewedItemsCount?: number,
  templateType?: string,
  countryCode?: string
) {
  if (!isPostHogConfigured()) return;

  posthog.capture("lightbox_closed", {
    media_id: String(mediaId),
    time_spent_seconds: timeSpentSeconds,
    viewed_items_count: viewedItemsCount,
    template_type: templateType || "BioLinkTemplate",
    country_code: countryCode || localStorage.getItem("test_country") || undefined,
    page_path: window.location.pathname,
    page_url: window.location.href,
    timestamp: new Date().toISOString(),
    user_region: getUserRegionForTracking(),
  });
}

export function trackLightboxNavigationPostHog(
  direction: "prev" | "next",
  fromMediaId: string | number,
  toMediaId: string | number,
  templateType?: string,
  countryCode?: string
) {
  if (!isPostHogConfigured()) return;

  posthog.capture("lightbox_navigation", {
    direction,
    from_media_id: String(fromMediaId),
    to_media_id: String(toMediaId),
    template_type: templateType || "BioLinkTemplate",
    country_code: countryCode || localStorage.getItem("test_country") || undefined,
    page_path: window.location.pathname,
    page_url: window.location.href,
    timestamp: new Date().toISOString(),
    user_region: getUserRegionForTracking(),
  });
}

/** Surfaces for `base_template_ui_click` (header, footer, lightbox, etc.) */
export type BaseTemplateClickSurface =
  | "header"
  | "nav_desktop"
  | "nav_mobile"
  | "mobile_menu"
  | "hero"
  | "sobre_mi"
  | "footer"
  | "lightbox"
  | "page";

export type BaseTemplateAnalyticsName =
  | "BaseTemplate"
  | "BaseTemplateBlack"
  | "BaseTemplateInternational"
  | "BaseTemplateInternationalDual"
  | "BaseTemplateArDual";

// ── Page-session timer (module-level) ────────────────────────────────────────
// Track cuándo el visitor entró a la "session" actual (no la PostHog session,
// la "tab session" — un page-load). Cualquier track* helper puede sumar
// `time_on_page_seconds` automáticamente sin que el call site tenga que
// llevar la cuenta. Re-stamp on pageshow (back/forward cache) para que un
// visitor que volvió por gesto BFCache no muestre tiempos infinitos.
let _pageEnteredAt: number = Date.now();
if (typeof window !== "undefined") {
  window.addEventListener("pageshow", (e) => {
    if ((e as PageTransitionEvent).persisted) {
      _pageEnteredAt = Date.now();
    }
  });
}
export function getTimeOnPageSecondsPostHog(): number {
  return Math.max(0, Math.round((Date.now() - _pageEnteredAt) / 1000));
}

export function trackBaseTemplateUIClickPostHog(params: {
  templateName: BaseTemplateAnalyticsName;
  elementId: string;
  label: string;
  surface: BaseTemplateClickSurface;
  href?: string;
  countryCode?: string;
  extra?: Record<string, string | number | boolean | undefined>;
}) {
  if (!isPostHogConfigured()) return;

  const geo = getWorkerGeoAnalyticsProps();
  const geoPayload = Object.fromEntries(
    Object.entries(geo).filter(
      ([, v]) => v !== undefined && v !== "",
    ),
  ) as Record<string, string>;

  const slackSession = getPostHogSlackSessionPropertiesPostHog();

  const basePayload = {
    template_type: params.templateName,
    element_id: params.elementId,
    label: params.label,
    surface: params.surface,
    href: params.href,
    country_code:
      params.countryCode ||
      localStorage.getItem("test_country") ||
      undefined,
    page_path: window.location.pathname,
    page_url: window.location.href,
    timestamp: new Date().toISOString(),
    // Tiempo desde page-load. Permite ver en PostHog "vieron el CTA, pero
    // clickearon a los 47s" vs "clickearon a los 3s" — proxy de hesitancy.
    time_on_page_seconds: getTimeOnPageSecondsPostHog(),
    user_region: getUserRegionForTracking(),
    ...geoPayload,
    ...slackSession,
    ...params.extra,
  };

  posthog.capture("base_template_ui_click", basePayload);

  /** Dedicated funnel event for Slack / real-time alerts (matches `base_template_hero_community_cta_click` dashboards). */
  const isHeroCommunityCta =
    params.surface === "hero" && params.elementId === "hero_cta_community";
  if (isHeroCommunityCta) {
    posthog.capture("base_template_hero_community_cta_click", {
      ...basePayload,
      ...getPostHogSlackSessionPropertiesPostHog({ replayNearNow: true }),
      cta_kind: "community",
      cta_destination: params.href,
    });
  }

  // attribution_events: click del hero CTA → reporte mensual del admin
  // (event_name 'hero_cta_clicked'). Solo clicks del hero. best-effort.
  if (params.surface === "hero") {
    captureAttribution("hero_cta_clicked", {
      template: params.templateName,
      heroCtaType: params.elementId,
      country: params.countryCode,
      metadata: { label: params.label, href: params.href },
    });
  }
}

/**
 * Hero CTA impression — fired once per page session when the hero CTA becomes
 * ≥50% visible. Pareja del click `base_template_ui_click` (surface=hero): permite
 * calcular "lo vio y NO lo clickeó" a nivel sesión, no solo el click.
 */
export function trackBaseTemplateHeroCtaImpressionPostHog(params: {
  templateName: BaseTemplateAnalyticsName;
  elementId?: string;
  label?: string;
  href?: string;
  countryCode?: string;
  extra?: Record<string, string | number | boolean | undefined>;
}) {
  if (!isPostHogConfigured()) return;

  const geo = getWorkerGeoAnalyticsProps();
  const geoPayload = Object.fromEntries(
    Object.entries(geo).filter(([, v]) => v !== undefined && v !== ""),
  ) as Record<string, string>;

  posthog.capture("base_template_hero_cta_impression", {
    template_type: params.templateName,
    element_id: params.elementId,
    label: params.label,
    surface: "hero",
    href: params.href,
    country_code:
      params.countryCode || localStorage.getItem("test_country") || undefined,
    page_path: window.location.pathname,
    page_url: window.location.href,
    timestamp: new Date().toISOString(),
    user_region: getUserRegionForTracking(),
    ...geoPayload,
    ...getPostHogSlackSessionPropertiesPostHog(),
    ...params.extra,
  });
}

// ─── Video Analytics ─────────────────────────────────────────────────────────

export function trackVideoPlayPostHog(
  videoId: string | number,
  videoTitle?: string,
  playbackId?: string,
  templateType?: string,
  countryCode?: string
) {
  if (!isPostHogConfigured()) return;

  posthog.capture("video_play", {
    video_id: String(videoId),
    video_title: videoTitle || `Video ${videoId}`,
    playback_id: playbackId ? playbackId.substring(0, 20) + "..." : undefined,
    template_type: templateType,
    country_code: countryCode || localStorage.getItem("test_country") || undefined,
    page_path: window.location.pathname,
    page_url: window.location.href,
    timestamp: new Date().toISOString(),
    user_region: getUserRegionForTracking(),
  });

  // attribution_events: play de video → reporte mensual (event_name
  // 'vip_video_play'). best-effort.
  captureAttribution("vip_video_play", {
    template: templateType,
    country: countryCode,
    metadata: { video_id: String(videoId), video_title: videoTitle },
  });
}

export function trackVideoPausePostHog(
  videoId: string | number,
  currentTimeSeconds?: number,
  templateType?: string,
  countryCode?: string
) {
  if (!isPostHogConfigured()) return;

  posthog.capture("video_pause", {
    video_id: String(videoId),
    current_time_seconds: currentTimeSeconds,
    template_type: templateType,
    country_code: countryCode || localStorage.getItem("test_country") || undefined,
    page_path: window.location.pathname,
    page_url: window.location.href,
    timestamp: new Date().toISOString(),
    user_region: getUserRegionForTracking(),
  });
}

export function trackVideoCompletePostHog(
  videoId: string | number,
  durationSeconds?: number,
  templateType?: string,
  countryCode?: string
) {
  if (!isPostHogConfigured()) return;

  posthog.capture("video_complete", {
    video_id: String(videoId),
    duration_seconds: durationSeconds,
    template_type: templateType,
    country_code: countryCode || localStorage.getItem("test_country") || undefined,
    page_path: window.location.pathname,
    page_url: window.location.href,
    timestamp: new Date().toISOString(),
    user_region: getUserRegionForTracking(),
  });
}

export function trackVideoProgressPostHog(
  videoId: string | number,
  progressPercent: number,
  currentTimeSeconds?: number,
  templateType?: string,
  countryCode?: string
) {
  if (!isPostHogConfigured()) return;

  posthog.capture("video_progress", {
    video_id: String(videoId),
    progress_percent: progressPercent,
    current_time_seconds: currentTimeSeconds,
    template_type: templateType,
    country_code: countryCode || localStorage.getItem("test_country") || undefined,
    page_path: window.location.pathname,
    page_url: window.location.href,
    timestamp: new Date().toISOString(),
    user_region: getUserRegionForTracking(),
  });
}

// ─── DualCTAVideoLanding (/video-landing) ────────────────────────────────────

export type DualCtaVideoLandingAction =
  | "page_enter_dual_cta_video"
  | "video_play"
  | "video_complete"
  | "cta_click_vip"
  | "cta_click_free_channel"
  | "checkout_opened"
  | "cta_checkout_error"
  | "mux_playback_id_missing"
  | "session_exit";

/** Stages for conversion_funnel_step — drop analysis dual VIP. */
const DUAL_CTA_FUNNEL_STAGE: Partial<
  Record<DualCtaVideoLandingAction, { funnel_step: string; funnel_stage: string }>
> = {
  page_enter_dual_cta_video: {
    funnel_step: "dual_page_enter",
    funnel_stage: "1_enter",
  },
  video_play: { funnel_step: "dual_video_play", funnel_stage: "2_video" },
  video_complete: {
    funnel_step: "dual_video_complete",
    funnel_stage: "2b_video_complete",
  },
  cta_click_vip: { funnel_step: "dual_vip_click", funnel_stage: "3_vip_intent" },
  checkout_opened: {
    funnel_step: "dual_checkout_opened",
    funnel_stage: "4_checkout",
  },
  cta_checkout_error: {
    funnel_step: "dual_checkout_error",
    funnel_stage: "4_checkout_fail",
  },
  cta_click_free_channel: {
    funnel_step: "dual_free_click",
    funnel_stage: "3b_free",
  },
  session_exit: { funnel_step: "dual_session_exit", funnel_stage: "exit" },
  mux_playback_id_missing: {
    funnel_step: "dual_mux_missing",
    funnel_stage: "2_video_fail",
  },
};

/**
 * Evento único y filtrable por `action` para DualCTA / USExclusiveLanding.
 * Lleva contexto completo de sesión (replay URL, distinct id), UTM y geo del Worker,
 * para correlacionar el funnel VIP en PostHog.
 *
 * También emite `conversion_funnel_step` con stages 1→4 + exit drop_reason
 * para medir dónde cae el visitante.
 *
 * `template_type` toma `props.template` / `template_type` cuando el host lo pasa
 * (USExclusiveLanding en `/` vs DualCTAVideoLanding en `/membership`).
 */
export function trackDualCtaVideoLandingPostHog(
  action: DualCtaVideoLandingAction,
  props?: Record<string, unknown>,
) {
  if (!isPostHogConfigured() || typeof window === "undefined") return;
  const slack = getPostHogSlackSessionPropertiesPostHog();
  const geo = getWorkerGeoAnalyticsProps();
  const geoFlat = Object.fromEntries(
    Object.entries(geo).filter(([, v]) => v !== undefined && v !== ""),
  ) as Record<string, string>;
  const templateFromProps =
    typeof props?.template === "string" && props.template.trim()
      ? props.template.trim()
      : typeof props?.template_type === "string" && props.template_type.trim()
        ? props.template_type.trim()
        : "DualCTAVideoLanding";

  const baseProps = {
    action,
    template_type: templateFromProps,
    page_path: window.location.pathname,
    page_url: window.location.href,
    user_region: getUserRegionForTracking(),
    test_country: localStorage.getItem("test_country"),
    timestamp: new Date().toISOString(),
    ...parseUTMFromSearch(window.location.search),
    ...geoFlat,
    ...slack,
    ...(props ?? {}),
  };

  posthog.capture("dual_cta_video_landing", baseProps);

  const stage = DUAL_CTA_FUNNEL_STAGE[action];
  if (stage) {
    posthog.capture("conversion_funnel_step", {
      ...stage,
      funnel_goal: "dual_membership_vip",
      dual_action: action,
      // drop_reason / abandon_segment vienen en props de session_exit
      drop_reason:
        typeof props?.drop_reason === "string" ? props.drop_reason : undefined,
      abandon_segment:
        typeof props?.abandon_segment === "string"
          ? props.abandon_segment
          : undefined,
      checkout_error:
        typeof props?.error === "string" ? props.error : undefined,
      gateway:
        typeof props?.gateway === "string" ? props.gateway : undefined,
      dual_variant_id:
        typeof props?.dual_variant_id === "string"
          ? props.dual_variant_id
          : undefined,
      country:
        typeof props?.country === "string" ? props.country : undefined,
      template_type: templateFromProps,
      page_path: window.location.pathname,
      ...slack,
    });
  }
}

/**
 * Flush best-effort antes de `location.assign` a Stripe — sin esto, VIP click /
 * checkout_opened / vip_checkout_started se pierden en la navegación.
 */
export function flushPostHogBeforeNavigationPostHog(timeoutMs = 400): Promise<void> {
  if (!isPostHogConfigured() || typeof window === "undefined") return Promise.resolve();
  try {
    const ph = posthog as {
      flush?: () => void | Promise<void>;
    };
    if (typeof ph.flush === "function") {
      const p = ph.flush();
      if (p && typeof (p as Promise<void>).then === "function") {
        return Promise.race([
          p as Promise<void>,
          new Promise<void>((resolve) => setTimeout(resolve, timeoutMs)),
        ]);
      }
    }
  } catch {
    /* best-effort */
  }
  return new Promise((resolve) => setTimeout(resolve, Math.min(timeoutMs, 120)));
}

/**
 * Registra como super-propiedades el estado de la landing /video-landing
 * (toggle del CTA gratis, disponibilidad de video y URL gratis) para que
 * TODOS los eventos posteriores de la sesión lo lleven y se pueda segmentar.
 */
export function registerDualCtaVideoLandingContextPostHog(props: {
  dual_cta_free_enabled: boolean;
  dual_cta_has_playback_id: boolean;
  dual_cta_has_free_url: boolean;
}): void {
  if (!isPostHogConfigured()) return;
  posthog.register(props);
}

/**
 * Registra como super-propiedades la variante A/B activa del DualCTAVideoLanding
 * para que TODOS los events post-resolución la lleven (pageview, video_play,
 * video_complete, cta_click, session_exit, vip_checkout_started, etc.).
 *
 * Llamado una sola vez al montar el componente con el resultado de
 * `__TEMPLATE_CONFIG__.dualVariant`. Si la variante es null/undefined (no
 * había assignment para este geo), NO registra nada — los events se ven
 * sin estas props (filtrables como "control / sin variante").
 */
export function registerDualCtaVariantContextPostHog(props: {
  dual_variant_id: string;
  dual_variant_geo_scope?: string;
  dual_variant_geo_code?: string;
  dual_ab_bucket?: string;
  dual_variant_stripe_price_id?: string;
  dual_variant_mp_label?: string;
  dual_variant_price_usd?: number;
}): void {
  if (!isPostHogConfigured()) return;
  // Filtra undefined → posthog.register no llora con keys vacías y queda más
  // limpio el panel de event properties.
  const clean: Record<string, string | number> = {};
  for (const [k, v] of Object.entries(props)) {
    if (v != null && v !== "") clean[k] = v as string | number;
  }
  posthog.register(clean);
}

// ─── Admin geo simulation ────────────────────────────────────────────────────

/**
 * Marca como super-propiedades que la sesión está en modo simulación de geo de
 * super admin. Registra `admin_geo_simulation=true` + `simulated_*` para que
 * TODOS los eventos posteriores lo lleven: así se validan las funnels (los
 * eventos SÍ se ven) pero se pueden excluir de los dashboards reales con el
 * filtro `admin_geo_simulation is not set`. Se llama al montar el widget con la
 * simulación activa; se limpia con clearAdminGeoSimulationContextPostHog al salir.
 */
export function registerAdminGeoSimulationContextPostHog(props: {
  simulated_country: string;
  simulated_state?: string;
  simulated_province?: string;
  simulated_city?: string;
  simulated_force_variant?: string;
}): void {
  if (!isPostHogConfigured()) return;
  const clean: Record<string, string | boolean> = { admin_geo_simulation: true };
  for (const [k, v] of Object.entries(props)) {
    if (typeof v === "string" && v.trim()) clean[k] = v.trim();
  }
  posthog.register(clean);
}

/** Quita las super-props de simulación al salir del modo (o al deshidratar). */
export function clearAdminGeoSimulationContextPostHog(): void {
  if (!isPostHogConfigured()) return;
  for (const key of [
    "admin_geo_simulation",
    "simulated_country",
    "simulated_state",
    "simulated_province",
    "simulated_city",
    "simulated_force_variant",
  ]) {
    posthog.unregister(key);
  }
}

// ─── BioTemplatePremium Checkout Flow ────────────────────────────────────────

export function trackPlanButtonClickPostHog(
  planTier: "standard" | "vip" | "free",
  price: number,
  currency: string,
  flowName: string,
  country?: string,
  hasVideo?: boolean,
  /** Default keeps dashboards that filter on BioTemplatePremium unchanged. */
  templateType: string = "BioTemplatePremium",
) {
  if (!isPostHogConfigured()) return;

  posthog.capture("plan_button_click", {
    plan_tier: planTier,
    price,
    currency,
    flow_name: flowName,
    country_code: country,
    has_video: hasVideo,
    template_type: templateType,
    page_path: window.location.pathname,
    page_url: window.location.href,
    timestamp: new Date().toISOString(),
    user_region: getUserRegionForTracking(),
  });
}

export function trackCheckoutPanelOpenedPostHog(
  planTier: "standard" | "vip",
  source: "direct_button" | "video_cta" | "discount_accepted",
  price: number,
  currency: string,
  flowName?: string,
  country?: string
) {
  if (!isPostHogConfigured()) return;

  posthog.capture("checkout_panel_opened", {
    plan_tier: planTier,
    source,
    price,
    currency,
    flow_name: flowName,
    country_code: country,
    template_type: "BioTemplatePremium",
    page_path: window.location.pathname,
    page_url: window.location.href,
    timestamp: new Date().toISOString(),
    user_region: getUserRegionForTracking(),
  });
}

export function trackCheckoutPanelClosedPostHog(
  planTier: "standard" | "vip",
  emailProvided: boolean,
  timeInCheckoutSeconds: number,
  reason: "user_closed" | "completed" | "error",
  flowName?: string,
  country?: string
) {
  if (!isPostHogConfigured()) return;

  posthog.capture("checkout_panel_closed", {
    plan_tier: planTier,
    email_provided: emailProvided,
    time_in_checkout_seconds: timeInCheckoutSeconds,
    reason,
    flow_name: flowName,
    country_code: country,
    template_type: "BioTemplatePremium",
    page_path: window.location.pathname,
    page_url: window.location.href,
    timestamp: new Date().toISOString(),
    user_region: getUserRegionForTracking(),
  });
}

export function trackEmailInputInteractionPostHog(
  action: "focus" | "blur",
  planTier?: "standard" | "vip",
  hasValue?: boolean,
  flowName?: string,
  country?: string
) {
  if (!isPostHogConfigured()) return;

  posthog.capture("email_input_interaction", {
    action,
    plan_tier: planTier,
    has_value: hasValue,
    flow_name: flowName,
    country_code: country,
    template_type: "BioTemplatePremium",
    page_path: window.location.pathname,
    page_url: window.location.href,
    timestamp: new Date().toISOString(),
    user_region: getUserRegionForTracking(),
  });
}

export function trackEmailValidationErrorPostHog(
  errorType: "empty" | "invalid",
  planTier?: "standard" | "vip",
  flowName?: string,
  country?: string
) {
  if (!isPostHogConfigured()) return;

  posthog.capture("email_validation_error", {
    error_type: errorType,
    plan_tier: planTier,
    flow_name: flowName,
    country_code: country,
    template_type: "BioTemplatePremium",
    page_path: window.location.pathname,
    page_url: window.location.href,
    timestamp: new Date().toISOString(),
    user_region: getUserRegionForTracking(),
  });
}

export function trackCheckoutAbandonmentPostHog(
  planTier: "standard" | "vip",
  emailProvided: boolean,
  timeInCheckoutSeconds: number,
  flowName?: string,
  country?: string
) {
  if (!isPostHogConfigured()) return;

  posthog.capture("checkout_abandonment", {
    plan_tier: planTier,
    email_provided: emailProvided,
    time_in_checkout_seconds: timeInCheckoutSeconds,
    flow_name: flowName,
    country_code: country,
    template_type: "BioTemplatePremium",
    page_path: window.location.pathname,
    page_url: window.location.href,
    timestamp: new Date().toISOString(),
    user_region: getUserRegionForTracking(),
  });
}

// ─── Purchase Revenue Tracking ───────────────────────────────────────────────

export function trackPurchaseWithRevenuePostHog(
  purchaseId: string,
  revenue: number,
  currency: string,
  planTier?: string,
  planName?: string,
  paymentMethod?: string,
  isSubscription?: boolean,
  subscriptionId?: string,
  userId?: string,
  country?: string,
  stripeSessionId?: string
) {
  if (!isPostHogConfigured()) return;

  posthog.capture("purchase_with_revenue", {
    purchase_id: purchaseId,
    revenue,
    currency: currency.toUpperCase(),
    plan_tier: planTier,
    plan_name: planName,
    payment_method: paymentMethod,
    is_subscription: isSubscription,
    subscription_id: subscriptionId,
    user_id: userId,
    country_code: country,
    stripe_session_id: stripeSessionId,
    template_type: "BioTemplatePremium",
    page_path: window.location.pathname,
    page_url: window.location.href,
    timestamp: new Date().toISOString(),
    user_region: getUserRegionForTracking(),
  });

  trackConversionPostHog("purchase", "premium_subscription", revenue, currency);
}

export function trackSubscriptionStartedPostHog(
  subscriptionId: string,
  planTier: string,
  price: number,
  currency: string,
  country?: string,
  userId?: string
) {
  if (!isPostHogConfigured()) return;

  posthog.capture("subscription_started", {
    subscription_id: subscriptionId,
    plan_tier: planTier,
    price,
    currency: currency.toUpperCase(),
    country_code: country,
    user_id: userId,
    template_type: "BioTemplatePremium",
    page_path: window.location.pathname,
    page_url: window.location.href,
    timestamp: new Date().toISOString(),
    user_region: getUserRegionForTracking(),
  });
}

// ─── User Identification ─────────────────────────────────────────────────────

/**
 * Identify user by email (called at checkout when email is provided).
 * PostHog SDK auto-captures browser, language, timezone, screen_resolution.
 * Calling identify() links all prior anonymous events to this person profile.
 */
export function identifyUserByEmailPostHog(
  email: string,
  properties?: Record<string, unknown>
) {
  if (!isPostHogConfigured()) return;

  posthog.identify(email, {
    email,
    ...properties,
    user_region: getUserRegionForTracking(),
    test_country: localStorage.getItem("test_country") || null,
  });
}

// ─── MediaKit Tracking (/mediakit, /mediakit-intro) ──────────────────────────

export type MediaKitVariant = "intro" | "desktop";
export type MediaKitNavMethod = "button" | "keyboard" | "swipe" | "thumbnail";

/** Fired once when the MediaKit viewer mounts */
export function trackMediaKitViewPostHog(
  variant: MediaKitVariant,
  totalPages: number,
  isFullscreenMode: boolean
) {
  if (!isPostHogConfigured()) return;

  posthog.capture("mediakit_view", {
    variant,
    total_pages: totalPages,
    is_fullscreen_mode: isFullscreenMode,
    page_path: window.location.pathname,
    referrer: document.referrer || null,
    timestamp: new Date().toISOString(),
    user_region: getUserRegionForTracking(),
    is_mobile:
      /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(
        navigator.userAgent
      ),
    viewport_size: `${window.innerWidth}x${window.innerHeight}`,
  });
}

/** Fired when the password gate is shown to a visitor */
export function trackMediaKitGateShownPostHog() {
  if (!isPostHogConfigured()) return;

  posthog.capture("mediakit_gate_shown", {
    page_path: window.location.pathname,
    referrer: document.referrer || null,
    timestamp: new Date().toISOString(),
    user_region: getUserRegionForTracking(),
  });
}

/** Fired on each password submission */
export function trackMediaKitPasswordAttemptPostHog(success: boolean) {
  if (!isPostHogConfigured()) return;

  posthog.capture("mediakit_password_attempt", {
    success,
    page_path: window.location.pathname,
    timestamp: new Date().toISOString(),
    user_region: getUserRegionForTracking(),
  });
}

/** Fired every time the visible page changes */
export function trackMediaKitPageViewPostHog(
  pageNumber: number,
  pageTitle: string,
  totalPages: number,
  navigationMethod: MediaKitNavMethod,
  isFullscreen: boolean,
  variant: MediaKitVariant
) {
  if (!isPostHogConfigured()) return;

  posthog.capture("mediakit_page_view", {
    page_number: pageNumber,
    page_title: pageTitle,
    total_pages: totalPages,
    navigation_method: navigationMethod,
    is_fullscreen: isFullscreen,
    is_first_page: pageNumber === 1,
    is_last_page: pageNumber === totalPages,
    variant,
    page_path: window.location.pathname,
    timestamp: new Date().toISOString(),
    user_region: getUserRegionForTracking(),
  });
}

/** Fired when user navigates between pages */
export function trackMediaKitNavigationPostHog(
  direction: "next" | "prev" | "jump",
  method: MediaKitNavMethod,
  fromPage: number,
  toPage: number,
  totalPages: number,
  isFullscreen: boolean,
  variant: MediaKitVariant
) {
  if (!isPostHogConfigured()) return;

  posthog.capture("mediakit_navigation", {
    direction,
    navigation_method: method,
    from_page: fromPage,
    to_page: toPage,
    total_pages: totalPages,
    is_fullscreen: isFullscreen,
    variant,
    page_path: window.location.pathname,
    timestamp: new Date().toISOString(),
    user_region: getUserRegionForTracking(),
  });
}

/** Fired when entering or exiting fullscreen */
export function trackMediaKitFullscreenTogglePostHog(
  action: "open" | "close",
  currentPage: number,
  variant: MediaKitVariant
) {
  if (!isPostHogConfigured()) return;

  posthog.capture("mediakit_fullscreen", {
    action,
    current_page: currentPage,
    variant,
    page_path: window.location.pathname,
    timestamp: new Date().toISOString(),
    user_region: getUserRegionForTracking(),
  });
}

/** Fired when the PDF download link is clicked */
export function trackMediaKitDownloadClickedPostHog(variant: MediaKitVariant) {
  if (!isPostHogConfigured()) return;

  posthog.capture("mediakit_download_clicked", {
    variant,
    page_path: window.location.pathname,
    timestamp: new Date().toISOString(),
    user_region: getUserRegionForTracking(),
  });

  posthog.capture("conversion_funnel_step", {
    funnel_step: "mediakit_download",
    variant,
    page_path: window.location.pathname,
  });
}

/** Fired once when all pages have been seen */
export function trackMediaKitCompletedPostHog(
  totalPages: number,
  timeSpentSeconds: number,
  variant: MediaKitVariant
) {
  if (!isPostHogConfigured()) return;

  posthog.capture("mediakit_completed", {
    total_pages: totalPages,
    time_spent_seconds: timeSpentSeconds,
    variant,
    page_path: window.location.pathname,
    timestamp: new Date().toISOString(),
    user_region: getUserRegionForTracking(),
  });
}

/** Fired when the user leaves the mediakit (unmount), with total session time */
export function trackMediaKitSessionEndPostHog(
  totalPages: number,
  pagesViewed: number,
  timeSpentSeconds: number,
  maxPageReached: number,
  variant: MediaKitVariant
) {
  if (!isPostHogConfigured()) return;

  posthog.capture("mediakit_session_end", {
    total_pages: totalPages,
    pages_viewed: pagesViewed,
    pages_viewed_pct: Math.round((pagesViewed / totalPages) * 100),
    max_page_reached: maxPageReached,
    time_spent_seconds: timeSpentSeconds,
    completed: pagesViewed === totalPages,
    variant,
    page_path: window.location.pathname,
    timestamp: new Date().toISOString(),
    user_region: getUserRegionForTracking(),
  });
}

// ─── VIP Custom Payment Funnel (/vip/:nombre) ────────────────────────────────

export function trackVipPageViewPostHog(
  nombre: string,
  country: string,
  isAvailable: boolean,
  paymentMethod: "mercadopago" | "stripe"
) {
  if (!isPostHogConfigured()) return;

  posthog.capture("vip_page_view", {
    vip_nombre: nombre,
    country_code: country,
    is_available: isAvailable,
    payment_method: paymentMethod,
    page_path: window.location.pathname,
    page_url: window.location.href,
    referrer: document.referrer,
    timestamp: new Date().toISOString(),
    user_region: getUserRegionForTracking(),
    is_mobile:
      /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(
        navigator.userAgent
      ),
  });
}

export function trackVipUnavailablePostHog(
  nombre: string,
  country: string,
  paymentMethod: "mercadopago" | "stripe"
) {
  if (!isPostHogConfigured()) return;

  posthog.capture("vip_unavailable_shown", {
    vip_nombre: nombre,
    country_code: country,
    payment_method: paymentMethod,
    page_path: window.location.pathname,
    timestamp: new Date().toISOString(),
    user_region: getUserRegionForTracking(),
  });
}

export function trackVipStepChangePostHog(
  nombre: string,
  from: string,
  to: string,
  country: string,
  currency: "ARS" | "USD",
  amount?: number
) {
  if (!isPostHogConfigured()) return;

  posthog.capture("vip_step_change", {
    vip_nombre: nombre,
    from_step: from,
    to_step: to,
    country_code: country,
    currency,
    amount,
    page_path: window.location.pathname,
    timestamp: new Date().toISOString(),
    user_region: getUserRegionForTracking(),
  });
}

export function trackVipAmountSelectedPostHog(
  nombre: string,
  amount: number,
  currency: "ARS" | "USD",
  country: string
) {
  if (!isPostHogConfigured()) return;

  posthog.capture("vip_amount_selected", {
    vip_nombre: nombre,
    amount,
    currency,
    country_code: country,
    page_path: window.location.pathname,
    timestamp: new Date().toISOString(),
    user_region: getUserRegionForTracking(),
  });
}

export function trackVipCheckoutStartedPostHog(
  nombre: string,
  amount: number,
  currency: "ARS" | "USD",
  country: string,
  paymentMethod: "mercadopago" | "stripe",
  hasEmail: boolean,
  hasName: boolean,
  hasMessage: boolean,
  variant?: string
) {
  if (!isPostHogConfigured()) return;

  posthog.capture("vip_checkout_started", {
    vip_nombre: nombre,
    amount,
    currency,
    country_code: country,
    payment_method: paymentMethod,
    has_email: hasEmail,
    has_name: hasName,
    has_message: hasMessage,
    checkout_variant: variant || null,
    funnel_step: "checkout_initiated",
    page_path: window.location.pathname,
    timestamp: new Date().toISOString(),
    user_region: getUserRegionForTracking(),
  });

  posthog.capture("conversion_funnel_step", {
    funnel_step: "vip_checkout_started",
    vip_nombre: nombre,
    amount,
    currency,
    country_code: country,
    payment_method: paymentMethod,
    checkout_variant: variant || null,
  });
}

export function trackVipPaymentResultPostHog(
  nombre: string,
  result: "success" | "error" | "pending",
  amount: number,
  currency: "ARS" | "USD",
  country: string,
  paymentMethod: "mercadopago" | "stripe",
  donationId?: string | null,
  errorMessage?: string,
  variant?: string
) {
  if (!isPostHogConfigured()) return;

  posthog.capture("vip_payment_result", {
    vip_nombre: nombre,
    result,
    amount,
    currency,
    country_code: country,
    payment_method: paymentMethod,
    donation_id: donationId,
    error_message: result === "error" ? errorMessage : undefined,
    checkout_variant: variant || null,
    page_path: window.location.pathname,
    timestamp: new Date().toISOString(),
    user_region: getUserRegionForTracking(),
  });

  if (result === "success") {
    posthog.capture("conversion", {
      conversion_type: "vip_payment",
      value: amount,
      currency,
      vip_nombre: nombre,
      country_code: country,
      payment_method: paymentMethod,
      donation_id: donationId,
      checkout_variant: variant || null,
    });
  }
}

/**
 * Tras completar perfil post-compra (session_id en query). Correlación soporte/disputas:
 * persona PostHog ↔ sesión de Checkout en Stripe ↔ metadata de membresía.
 */
export function capturePostPurchaseProfileCompletedPostHog(payload: {
  plan_tier: string | null;
  from_vip: boolean;
  from_basic: boolean;
  from_standard?: boolean;
  stripe_session_id: string | null;
  mercadopago_subscription_id?: string | null;
}) {
  if (!isPostHogConfigured()) return;

  posthog.capture("post_purchase_profile_completed", {
    plan_tier: payload.plan_tier || undefined,
    from_vip: payload.from_vip,
    from_basic: payload.from_basic,
    from_standard: payload.from_standard ?? false,
    has_stripe_session: Boolean(payload.stripe_session_id),
    stripe_session_id: payload.stripe_session_id || undefined,
    has_mercadopago_subscription: Boolean(payload.mercadopago_subscription_id),
    mercadopago_subscription_id: payload.mercadopago_subscription_id || undefined,
    page_path: window.location.pathname,
  });
}

// Export PostHog instance for direct access
export { posthog };
